import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate } from 'react-router-dom';
import AppLayout from './AppLayout.jsx';
import PublicProfilePreviewCofradia from './PublicProfilePreviewCofradia.jsx';
import ShareProfileButton from './ShareProfileButton.jsx';
import ProfileTipsCarousel from './ProfileTipsCarousel.jsx';
import { buildPublicProfileShareUrl } from '../utils/publicProfileShareUrl.js';
import SkillsEditor from './SkillsEditor';
import ImageCropper from './ImageCropper';
import LocationMap from './LocationMap.jsx';
import ComingSoonSection from './ComingSoonSection.jsx';
import { useMvpMode } from '../config/mvp.jsx';
import { useAuth } from '../contexts/AuthContext';
import { trackProfileUpdate,
  trackProfileCompletion,
  trackAvatarUpload,
  trackPhotoUpload } from '../services/analytics';
import { getCofradiaPlaceholderIcon } from '../utils/cofradiaPlaceholderIcon';
import { getPerfilDisplayName } from '../utils/miEntidadNombreDefecto';
import { getCofradiaBioHistoriaCopy } from '../utils/cofradiaHistoriaBioCopy';
import {
  getCofradiaContactoEmailPlaceholder,
  getCofradiaRedSocialHandlePlaceholder,
  getCofradiaGaleriaFotosConsejoLinea1,
  getCofradiaYoutubeTituloPlaceholder,
  } from '../utils/cofradiaContactoCopy';
import { formatFiestaLocalRango } from '../utils/fiestaLocalDisplay';
import { mergeContactoPublicoForForm } from '../utils/contactoPublicoForm.js';
import { isLocalizacionCompleta, parseNominatimAddress } from '../utils/nominatimAddress.js';
import { resolveUploadsUrl } from '../utils/staticUrl.js';
import { cofradiasAPI,
  authAPI,
  avalesAPI,
  bandasAPI,
  resenasAPI } from '../services/api';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Textarea,
  Button,
  Icon,
  SimpleGrid,
  Card,
  Badge,
  Flex,
  IconButton,
  Image,
  Grid,
  GridItem,
  NumberInput,
  Alert,
  Progress,
  Tabs,
  Accordion,
  useDisclosure,
  Spinner,
  Separator,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { TabList, TabPanels, Tab, TabPanel, tabsControlledValue, tabsOnValueChange, Switch} from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { InputGroup, InputLeftElement, InputRightElement } from '@/components/ui/input-group';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaUser,
  FaCamera,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaCalendarAlt,
  FaEuroSign,
  FaRoute,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSave,
  FaMusic,
  FaChurch,
  FaStar,
  FaRegStar,
  FaIdCard,
  FaShieldAlt,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaArrowRight,
  FaInfoCircle,
  FaLightbulb,
  FaUsers,
  FaHistory,
  FaGavel,
  FaEye,
  FaEyeSlash,
  FaTimes,
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaPlay,
  FaCrop,
  FaHandshake,
  FaAward,
  FaSearch,
  FaCheck,
  FaComments,
  FaGlobe,
  FaTiktok,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const COFRADIA_VARIANTE_DEFAULT = 'RELIGIOSO';

/** Etiqueta de checklist / obligatorio según variante de perfil (RELIGIOSO sin cambios de copy base). */
const resolveCofradiaCheckLabel = (check, variante = COFRADIA_VARIANTE_DEFAULT) => {
  if (typeof check.labelForVariant === 'function') return check.labelForVariant(variante);
  return check.label;
};

/** Textos del recuadro “Completa tu perfil…” y avisos relacionados (RELIGIOSO = copy original). */
const getCofradiaVisibilidadCopy = (variante) => {
  const v = variante || COFRADIA_VARIANTE_DEFAULT;
  if (v === 'AYUNTAMIENTO') {
    return {
      checklistTitle: 'Completa el perfil público del municipio',
      checklistSubtitle:
        'El porcentaje recoge la pestaña Perfil público adaptada a ayuntamientos. Los apartados obligatorios deben estar completos para poder guardar; el resto mejora cómo bandas y entidades locales os encuentran para actos oficiales y colaboraciones.',
      consejoBody:
        'Aún queda información por completar en tu perfil. Cuanto más datos añadas, mejor podrás posicionarlo y ganar visibilidad ante el resto de usuarios.',
      validacionTitle: 'Completa tu perfil para solicitar la validación',
      validacionBody:
        'Tu perfil no será visible para otros usuarios hasta que sea validado. Completa los campos obligatorios y guarda los cambios para iniciar el proceso de validación automáticamente.',
    };
  }
  if (v === 'ASOCIACION_CULTURAL') {
    return {
      checklistTitle: 'Aumenta la visibilidad de tu asociación cultural',
      checklistSubtitle:
        'El porcentaje incluye los apartados de Perfil público pensados para asociaciones culturales. Los obligatorios permiten guardar; el resto refuerza vuestra presencia ante bandas y colaboradores.',
      consejoBody:
        'Aún queda información por completar en tu perfil. Cuanto más datos añadas, mejor podrás posicionarlo y ganar visibilidad ante el resto de usuarios.',
      validacionTitle: 'Completa tu perfil para solicitar la validación',
      validacionBody:
        'Tu perfil no será visible para otros usuarios hasta que sea validado. Completa los campos obligatorios (incluida la identidad de la asociación) y guarda los cambios para iniciar el proceso de validación automáticamente.',
    };
  }
  if (v === 'OTRO') {
    return {
      checklistTitle: 'Completa la ficha de tu fundación u organismo',
      checklistSubtitle:
        'El porcentaje mide el Perfil público para fundaciones y otras entidades. Los obligatorios permiten guardar; el resto mejora la confianza y el posicionamiento frente a bandas y colaboradores.',
      consejoBody:
        'Aún queda información por completar en tu perfil. Cuanto más datos añadas, mejor podrás posicionarlo y ganar visibilidad ante el resto de usuarios.',
      validacionTitle: 'Completa tu perfil para solicitar la validación',
      validacionBody:
        'Tu perfil no será visible para otros usuarios hasta que sea validado. Completa los campos obligatorios y guarda los cambios para iniciar el proceso de validación automáticamente.',
    };
  }
  return {
    checklistTitle: 'Completa tu perfil para aumentar tu visibilidad',
    checklistSubtitle:
      'El porcentaje incluye todos los apartados de la pestaña Perfil público. Los marcados como obligatorios deben estar completos para poder guardar; el resto mejora tu visibilidad y posicionamiento.',
    consejoBody:
      'Aún queda información por completar en tu perfil. Cuanto más datos añadas, mejor podrás posicionarlo y ganar visibilidad ante el resto de usuarios.',
    validacionTitle: 'Completa tu perfil para solicitar la validación',
    validacionBody:
      'Tu perfil no será visible para otros usuarios hasta que sea validado. Completa todos los campos obligatorios y guarda los cambios para iniciar el proceso de validación automáticamente.',
  };
};

/** Obligatorios para guardar el perfil (validación); subconjunto de COFRADIA_PROFILE_COMPLETENESS_CHECKS */
const COFRADIA_MANDATORY_PROFILE_CHECKS = [
  {
    label: 'Nombre de la cofradía',
    labelForVariant: (v) =>
      v === 'RELIGIOSO' ? 'Nombre de la cofradía' : 'Nombre oficial de la entidad',
    test: (d) => !!d?.nombre?.trim(),
  },
  { label: 'Tipo de entidad', test: (d) => !!(d?.tipoEntidadId && String(d.tipoEntidadId).trim()) },
  { label: 'Localización', test: isLocalizacionCompleta },
  { label: 'Teléfono de contacto', test: (d) => !!d?.contactoPublico?.telefono?.trim() },
  { label: 'Email de contacto', test: (d) => !!d?.contactoPublico?.email?.trim() },
];

/**
 * Todos los apartados de la pestaña Perfil público que alimentan la barra % y el checklist.
 * mandatoryForSave: bloquea guardar si falta (mismo criterio que COFRADIA_MANDATORY_PROFILE_CHECKS).
 * variantsOnly: si existe, el ítem solo cuenta para esas variantes (RELIGIOSO, AYUNTAMIENTO, ASOCIACION_CULTURAL, OTRO).
 */
const COFRADIA_PROFILE_COMPLETENESS_CHECKS = [
  ...COFRADIA_MANDATORY_PROFILE_CHECKS.map((c) => ({ ...c, mandatoryForSave: true })),
  {
    label: 'Historia de la cofradía',
    labelForVariant: (v) => {
      if (v === 'AYUNTAMIENTO') return 'Historia o relato del municipio / corporación local';
      if (v === 'ASOCIACION_CULTURAL') return 'Historia o descripción de la asociación';
      if (v === 'OTRO') return 'Misión, objetivos o historia de la entidad / fundación';
      return 'Historia de la cofradía';
    },
    test: (d) => !!d?.bio?.trim(),
    mandatoryForSave: false,
  },
  {
    label: 'Logo o imagen de perfil',
    labelForVariant: (v) => {
      if (v === 'AYUNTAMIENTO') return 'Escudo o imagen institucional del perfil';
      if (v === 'ASOCIACION_CULTURAL' || v === 'OTRO') return 'Logo o imagen corporativa del perfil';
      return 'Logo o imagen de perfil';
    },
    test: (d) => !!d?.avatar,
    mandatoryForSave: false,
  },
  {
    label: 'Imagen de cabecera o fotos en galería',
    labelForVariant: (v) => {
      if (v === 'AYUNTAMIENTO') return 'Imagen corporativa de cabecera o galería de fotos';
      if (v === 'ASOCIACION_CULTURAL') return 'Cabecera o galería (sede, eventos o actividades)';
      if (v === 'OTRO') return 'Cabecera o galería institucional';
      return 'Imagen de cabecera o fotos en galería';
    },
    test: (d) => !!(d?.fondoHeaderImagen || (Array.isArray(d?.fotos) && d.fotos.length > 0)),
    mandatoryForSave: false,
  },
  {
    label: 'Sitio web o al menos una red social',
    labelForVariant: (v) => {
      if (v === 'AYUNTAMIENTO') return 'Web municipal o al menos una red oficial';
      if (v === 'ASOCIACION_CULTURAL') return 'Web o redes de la asociación';
      if (v === 'OTRO') return 'Web institucional o al menos una red social';
      return 'Sitio web o al menos una red social';
    },
    test: (d) =>
      !!d?.contactoPublico?.website?.trim() ||
      !!d?.redesSociales?.instagram?.trim() ||
      !!d?.redesSociales?.facebook?.trim() ||
      !!d?.redesSociales?.youtube?.trim() ||
      !!d?.redesSociales?.twitter?.trim() ||
      !!d?.redesSociales?.tiktok?.trim(),
    mandatoryForSave: false,
  },
  {
    label: 'Al menos un vídeo en el perfil',
    labelForVariant: (v) =>
      v === 'RELIGIOSO' ? 'Al menos un vídeo en el perfil' : 'Vídeo de presentación o actividades recientes',
    test: (d) => Array.isArray(d?.videos) && d.videos.length > 0,
    mandatoryForSave: false,
  },
  {
    label: 'Información de la hermandad (sede, cultos, etc.)',
    variantsOnly: ['RELIGIOSO'],
    test: (d) =>
      !!d?.sedeCanonica?.trim() ||
      !!d?.fechaFundacion?.trim() ||
      !!(d?.numeroHermanos != null && String(d.numeroHermanos).trim() !== '') ||
      !!d?.horariosCulto?.trim() ||
      !!d?.procesionesAnuales?.trim() ||
      !!d?.patrimonioArtistico?.trim(),
    mandatoryForSave: false,
  },
  {
    label: 'Actividades y cultos',
    variantsOnly: ['RELIGIOSO'],
    test: (d) => Array.isArray(d?.actividadesYCultos) && d.actividadesYCultos.length > 0,
    mandatoryForSave: false,
  },
  {
    label: 'Datos del municipio (habitantes, fiestas o patronazgo)',
    labelForVariant: () =>
      'Datos del municipio (habitantes, fiestas locales o patronazgo del pueblo)',
    variantsOnly: ['AYUNTAMIENTO'],
    test: (d) => {
      const fi = Array.isArray(d.fiestasLocales) && d.fiestasLocales.some((f) => f?.nombre?.trim());
      return (
        (d?.habitantesMunicipio != null && String(d.habitantesMunicipio).trim() !== '') ||
        fi ||
        !!d?.patronDelPueblo?.trim() ||
        !!d?.patronaDelPueblo?.trim()
      );
    },
    mandatoryForSave: false,
  },
  {
    label: 'Perfil cultural (ámbito, socios o actividades)',
    labelForVariant: () => 'Perfil cultural (ámbito, socios aprox. o actividades y proyectos)',
    variantsOnly: ['ASOCIACION_CULTURAL'],
    test: (d) =>
      !!d?.culturalAmbito?.trim() ||
      (d?.culturalNumeroSocios != null && String(d.culturalNumeroSocios).trim() !== '') ||
      !!d?.culturalActividadesDescripcion?.trim(),
    mandatoryForSave: false,
  },
  {
    label: 'Identidad y transparencia (CIF, biografía ampliada o web)',
    variantsOnly: ['OTRO'],
    test: (d) =>
      !!(d?.cif && String(d.cif).trim()) ||
      (d?.bio || '').trim().length >= 100 ||
      !!(d?.contactoPublico?.website && String(d.contactoPublico.website).trim()),
    mandatoryForSave: false,
  },
];

/** Ítems que entran en % y lista para esta variante (`variantsOnly` acota bloques específicos). */
const cofradiaProfileChecksForVariant = (variante) => {
  const v = variante || COFRADIA_VARIANTE_DEFAULT;
  return COFRADIA_PROFILE_COMPLETENESS_CHECKS.filter((c) => !c.variantsOnly || c.variantsOnly.includes(v));
};

/** Lat/lng desde la API (número, string o Decimal serializado). */
function parseCofradiaLatLngFromApi(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

function CofradiaPerfilContactoPublico({ cardBg, profileData, handleNestedInputChange, emailPlaceholder }) {
  return (
    <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
      <Card.Header>
        <Heading size="md">
          <Icon as={FaPhone} mr={2} color="purple.600" />
          Información de contacto
        </Heading>
        <Text fontSize="xs" color="gray.600" mt={2} lineHeight="short">
        El teléfono y el email no aparecerán en tu perfil público en Cadencya; los usamos para la gestión de la cuenta y para
          comunicarnos contigo cuando sea necesario.
        </Text>
      </Card.Header>
      <Card.Body>
        <VStack gap={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
            <Field.Root>
              <Field.Label>Teléfono</Field.Label>
              <Input
                value={profileData.contactoPublico.telefono}
                onChange={(e) => handleNestedInputChange('contactoPublico', 'telefono', e.target.value)}
                placeholder="+34 600 123 456"
                type="tel"
              />
            </Field.Root>
            <Field.Root>
              <Field.Label>Email</Field.Label>
              <Input
                value={profileData.contactoPublico.email}
                onChange={(e) => handleNestedInputChange('contactoPublico', 'email', e.target.value)}
                placeholder={emailPlaceholder}
                type="email"
              />
            </Field.Root>
          </SimpleGrid>
          <Field.Root>
            <Field.Label>Sitio web</Field.Label>
            <Input
              value={profileData.contactoPublico.website}
              onChange={(e) => handleNestedInputChange('contactoPublico', 'website', e.target.value)}
              placeholder="https://www.ejemplo.com"
              type="url"
            />
          </Field.Root>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}

function CofradiaPerfilLocalizacion({
  cardBg,
  textColor,
  profileData,
  locationSearchQuery,
  setLocationSearchQuery,
  locationSuggestions,
  showLocationSuggestions,
  setShowLocationSuggestions,
  isSearchingLocation,
  selectLocation,
}) {
  const suggestionBorder = useColorModeValue('gray.200', 'gray.600');
  const suggestionHover = useColorModeValue('gray.50', 'gray.700');
  const suggestionRowBorder = useColorModeValue('gray.100', 'gray.700');
  const locInfoBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
      <Card.Header>
        <Heading size="md">
          <Icon as={FaMapMarkerAlt} mr={2} color="purple.600" />
          Localización
        </Heading>
      </Card.Header>
      <Card.Body>
        <VStack gap={6}>
          <Field.Root>
            <Field.Label>Buscar ubicación</Field.Label>
            <Box position="relative">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaMapMarkerAlt} color="gray.400" />
                </InputLeftElement>
                <Input
                  value={locationSearchQuery}
                  onChange={(e) => setLocationSearchQuery(e.target.value)}
                  placeholder="Escribe una dirección, ciudad o lugar..."
                  onFocus={() => locationSuggestions.length > 0 && setShowLocationSuggestions(true)}
                />
                {isSearchingLocation && (
                  <InputRightElement>
                    <Spinner size="sm" color="purple.500" />
                  </InputRightElement>
                )}
              </InputGroup>

              {showLocationSuggestions && locationSuggestions.length > 0 && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  mt={1}
                  bg={cardBg}
                  border="1px solid"
                  borderColor={suggestionBorder}
                  borderRadius="md"
                  shadow="lg"
                  maxH="300px"
                  overflowY="auto"
                  zIndex={1000}
                >
                  {locationSuggestions.map((suggestion, index) => (
                    <Box
                      key={index}
                      p={3}
                      cursor="pointer"
                      _hover={{ bg: suggestionHover }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectLocation(suggestion);
                      }}
                      onClick={() => selectLocation(suggestion)}
                      borderBottom="1px solid"
                      borderColor={suggestionRowBorder}
                    >
                      <HStack gap={2}>
                        <Icon as={FaMapMarkerAlt} color="purple.500" />
                        <Text fontSize="sm">{suggestion.display_name}</Text>
                      </HStack>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Field.HelperText>
              Escribe para buscar tu ubicación. Las sugerencias aparecerán automáticamente.
            </Field.HelperText>
          </Field.Root>

          {(profileData.localizacion.ciudad || profileData.localizacion.provincia) && (
            <Box w="full" p={4} bg={locInfoBg} borderRadius="md">
              <Text fontSize="sm" fontWeight="medium" mb={3} color={textColor}>
                Información de la ubicación:
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                {profileData.localizacion.calle && (
                  <HStack>
                    <Text fontSize="xs" fontWeight="medium" color="gray.500">
                      Calle:
                    </Text>
                    <Text fontSize="xs">
                      {profileData.localizacion.calle} {profileData.localizacion.numero}
                    </Text>
                  </HStack>
                )}
                {profileData.localizacion.ciudad && (
                  <HStack>
                    <Text fontSize="xs" fontWeight="medium" color="gray.500">
                      Ciudad:
                    </Text>
                    <Text fontSize="xs">{profileData.localizacion.ciudad}</Text>
                  </HStack>
                )}
                {profileData.localizacion.provincia && (
                  <HStack>
                    <Text fontSize="xs" fontWeight="medium" color="gray.500">
                      Provincia:
                    </Text>
                    <Text fontSize="xs">{profileData.localizacion.provincia}</Text>
                  </HStack>
                )}
                {profileData.localizacion.codigoPostal && (
                  <HStack>
                    <Text fontSize="xs" fontWeight="medium" color="gray.500">
                      C.P.:
                    </Text>
                    <Text fontSize="xs">{profileData.localizacion.codigoPostal}</Text>
                  </HStack>
                )}
                {profileData.lat != null && profileData.lng != null && (
                  <HStack gridColumn={{ base: 1, md: '1 / 3' }}>
                    <Text fontSize="xs" fontWeight="medium" color="gray.500">
                      Coordenadas:
                    </Text>
                    <Text fontSize="xs">
                      {profileData.lat.toFixed(6)}, {profileData.lng.toFixed(6)}
                    </Text>
                  </HStack>
                )}
              </SimpleGrid>
            </Box>
          )}

          {profileData.localizacion?.ciudad && profileData.localizacion?.provincia && (
            <Box w="full">
              <Text fontSize="sm" fontWeight="medium" mb={3} color={textColor}>
                Vista previa de la ubicación:
              </Text>
              <LocationMap address={profileData.localizacion} height="250px" nombre={profileData.nombre} />
            </Box>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}

function CofradiaPerfilRedesSociales({
  cardBg,
  profileData,
  handleNestedInputChange,
  redSocialHandlePlaceholder,
}) {
  return (
    <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
      <Card.Header>
        <Heading size="md">
          <Icon as={FaInstagram} mr={2} color="purple.600" />
          Redes sociales
        </Heading>
        <Text fontSize="sm" color="gray.600">
          Añade tus redes sociales para que las bandas puedan conocerte mejor
        </Text>
      </Card.Header>
      <Card.Body>
        <VStack gap={4}>
          <Field.Root>
            <Field.Label>
              <HStack gap={2}>
                <Icon as={FaInstagram} color="pink.500" />
                <Text>Instagram</Text>
              </HStack>
            </Field.Label>
            <Input
              value={profileData.redesSociales.instagram}
              onChange={(e) => handleNestedInputChange('redesSociales', 'instagram', e.target.value)}
              placeholder={redSocialHandlePlaceholder}
            />
            <Field.HelperText>Introduce tu nombre de usuario de Instagram (con o sin @)</Field.HelperText>
          </Field.Root>

          <Field.Root>
            <Field.Label>
              <HStack gap={2}>
                <Icon as={FaFacebook} color="blue.500" />
                <Text>Facebook</Text>
              </HStack>
            </Field.Label>
            <Input
              value={profileData.redesSociales.facebook}
              onChange={(e) => handleNestedInputChange('redesSociales', 'facebook', e.target.value)}
              placeholder="Nombre de tu página de Facebook"
            />
            <Field.HelperText>Introduce el nombre de tu página de Facebook</Field.HelperText>
          </Field.Root>

          <Field.Root>
            <Field.Label>
              <HStack gap={2}>
                <Icon as={FaYoutube} color="red.500" />
                <Text>YouTube</Text>
              </HStack>
            </Field.Label>
            <Input
              value={profileData.redesSociales.youtube}
              onChange={(e) => handleNestedInputChange('redesSociales', 'youtube', e.target.value)}
              placeholder="Nombre de tu canal de YouTube"
            />
            <Field.HelperText>Introduce el nombre de tu canal de YouTube</Field.HelperText>
          </Field.Root>

          <Field.Root>
            <Field.Label>
              <HStack gap={2}>
                <Icon as={FaXTwitter} color="gray.800" />
                <Text>X</Text>
              </HStack>
            </Field.Label>
            <Input
              value={profileData.redesSociales.twitter || ''}
              onChange={(e) => handleNestedInputChange('redesSociales', 'twitter', e.target.value)}
              placeholder={redSocialHandlePlaceholder}
            />
            <Field.HelperText>Introduce tu usuario de X (con o sin @) o el enlace completo</Field.HelperText>
          </Field.Root>

          <Field.Root>
            <Field.Label>
              <HStack gap={2}>
                <Icon as={FaTiktok} color="gray.800" />
                <Text>TikTok</Text>
              </HStack>
            </Field.Label>
            <Input
              value={profileData.redesSociales.tiktok}
              onChange={(e) => handleNestedInputChange('redesSociales', 'tiktok', e.target.value)}
              placeholder={redSocialHandlePlaceholder}
            />
            <Field.HelperText>Introduce tu nombre de usuario de TikTok (con o sin @)</Field.HelperText>
          </Field.Root>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}

const ProfileCofradia = () => {
  const navigate = useNavigate();
  
  const { isMvpMode } = useMvpMode();
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null); // Ref para el input de la galería
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Estados para el editor de imágenes (cropper)
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperType, setCropperType] = useState(null); // 'avatar' o 'fondo'
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  
  // Estado para el modal de imagen
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setIsImageModalOpen(true);
  };
  
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // Referencias para navegación a campos
  const nombreRef = useRef(null);
  const responsableNombreRef = useRef(null);
  const responsableApellidosRef = useRef(null);
  const responsableEmailRef = useRef(null);
  const calleRef = useRef(null);
  const ciudadRef = useRef(null);
  const provinciaRef = useRef(null);
  
  // Estado específico para cofradías
  const [userType] = useState('cofradia');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const locationJustSelectedRef = useRef(false);
  
  // Estado para previsualización del perfil público
  const [showPublicPreview, setShowPublicPreview] = useState(false);
  
  // Estado para avales (endorsements)
  const [avalesOtorgados, setAvalesOtorgados] = useState([]);
  const [isLoadingAvales, setIsLoadingAvales] = useState(false);
  const [bandaSearchQuery, setBandaSearchQuery] = useState('');
  const [bandasSearchResults, setBandasSearchResults] = useState([]);
  const [isSearchingBandas, setIsSearchingBandas] = useState(false);
  const [selectedBandaForAval, setSelectedBandaForAval] = useState(null);
  const [avalTexto, setAvalTexto] = useState('');
  const [isCreatingAval, setIsCreatingAval] = useState(false);
  
  // Estados para añadir videos de YouTube
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  
  const [profileData, setProfileData] = useState({
    // Perfil público
    nombre: '',
    bio: '',
    avatar: null,
    fondoHeaderImagen: null, // Imagen de fondo personalizable para la card del header
    fotos: [],
    videos: [], // Array de {titulo, url} para videos de YouTube
    contactoPublico: {
      telefono: '',
      email: '',
      website: '',
    },
    redesSociales: {
      instagram: '',
      facebook: '',
      youtube: '',
      twitter: '',
      tiktok: '',
    },
    actividadesYCultos: [],
    tipoEntidadId: '',
    tipoEntidad: null,
    perfilVariante: 'RELIGIOSO',
    tipoGrupo: '',
    habitantesMunicipio: '',
    fiestasLocales: [],
    patronDelPueblo: '',
    patronaDelPueblo: '',
    culturalAmbito: '',
    culturalNumeroSocios: '',
    culturalActividadesDescripcion: '',
    localizacion: {
      calle: '',
      numero: '',
      ciudad: '',
      provincia: '',
      codigoPostal: '',
      pais: 'España',
    },
    lat: null,
    lng: null,
    
    // Reseñas (cargadas desde la API)
    resenas: [],
    solicitudesResenaPendientes: [],
    
    // Información para verificación
    cif: '',
    personaResponsable: {
      nombre: '',
      apellidos: '',
      dni: '',
      telefono: '',
      email: '',
    },
    fechasDisponibles: [],
    
    // Específico para cofradías
    tipoEventos: [],
    numeroHermanos: '',
    fechaFundacion: '',
    patrono: '',
    sedeCanonica: '',
    horariosCulto: '',
    procesionesAnuales: '',
    patrimonioArtistico: '',
  });

  const [errors, setErrors] = useState({});
  const [verificationStatus, setVerificationStatus] = useState('not_requested'); // 'not_requested', 'pending', 'verified', 'rejected'
  const [verificationCode, setVerificationCode] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [isChangingUsername, setIsChangingUsername] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  
  // Estado para detectar cambios sin guardar
  const [initialProfileData, setInitialProfileData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  const { user, logout, updateProfile, profile } = useAuth();

  /** Solo id de Cofradía (no user.id: la API pública es GET /cofradias/:cofradiaId). */
  const publicProfileShareUrl = useMemo(
    () => buildPublicProfileShareUrl('cofradia', profile?.id),
    [profile?.id],
  );

  // Cargar perfil desde la API
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const response = await cofradiasAPI.getMyProfile();
        if (response.data) {
          const data = response.data;
          const contactoMerged = mergeContactoPublicoForForm(
            data.contactoPublico,
            data.personaResponsable,
          );
          const tipoEnt = data.tipoEntidad || null;
          const tipoEntidadId = data.tipoEntidadId || tipoEnt?.id || '';
          const perfilVariante = tipoEnt?.variantePerfil || 'RELIGIOSO';
          const fiestasRaw = Array.isArray(data.fiestasLocales) ? data.fiestasLocales : [];
          setProfileData(prev => ({
            ...prev,
            nombre: data.nombre || '',
            bio: data.bio || '',
            avatar: data.avatar || null,
            fondoHeaderImagen: data.fondoHeaderImagen || null,
            fotos: data.fotos || [],
            videos: data.videos || [],
            contactoPublico: contactoMerged,
            redesSociales: data.redesSociales || prev.redesSociales,
            actividadesYCultos: data.actividadesYCultos || [],
            tipoEntidadId,
            tipoEntidad: tipoEnt,
            perfilVariante,
            tipoGrupo: data.tipoGrupo || tipoEnt?.nombre || '',
            habitantesMunicipio:
              data.habitantesMunicipio != null ? String(data.habitantesMunicipio) : '',
            fiestasLocales: fiestasRaw.map((f) => ({
              nombre: f?.nombre || '',
              mes: f?.mes != null && f?.mes !== '' ? String(f.mes) : '',
              dia: f?.dia != null && f?.dia !== '' ? String(f.dia) : '',
              fechaInicio: f?.fechaInicio ? String(f.fechaInicio).trim() : '',
              fechaFin: f?.fechaFin ? String(f.fechaFin).trim() : '',
              notas: f?.notas || '',
            })),
            patronDelPueblo: data.patronDelPueblo || '',
            patronaDelPueblo: data.patronaDelPueblo || '',
            culturalAmbito: data.culturalAmbito || '',
            culturalNumeroSocios:
              data.culturalNumeroSocios != null ? String(data.culturalNumeroSocios) : '',
            culturalActividadesDescripcion: data.culturalActividadesDescripcion || '',
            localizacion:
              data.localizacion != null && typeof data.localizacion === 'object'
                ? data.localizacion
                : prev.localizacion,
            lat: parseCofradiaLatLngFromApi(data.lat),
            lng: parseCofradiaLatLngFromApi(data.lng),
            cif: data.cif || '',
            personaResponsable: data.personaResponsable || prev.personaResponsable,
            numeroHermanos: data.numeroHermanos?.toString() || '',
            fechaFundacion: data.fechaFundacion || '',
            patrono: data.patrono || '',
            sedeCanonica: data.sedeCanonica || '',
            horariosCulto: data.horariosCulto || '',
            procesionesAnuales: data.procesionesAnuales || '',
            patrimonioArtistico: data.patrimonioArtistico || '',
            resenas: (data.resenasRecibidas || []).map(r => ({
              id: r.id,
              bandaName: r.autorBanda?.nombre || 'Anónimo',
              bandaAvatar: r.autorBanda?.avatar || null,
              eventoNombre: r.evento?.nombre || null,
              eventoFecha: r.evento?.fecha || null,
              ratingGeneral: r.ratingGeneral,
              // Calcular rating promedio de todas las puntuaciones
              averageRating: [
                r.ratingComunicacion,
                r.ratingFormalidad,
                r.ratingAtencion,
                r.ratingGeneral
              ].filter(Boolean).reduce((acc, val, _, arr) => acc + val / arr.length, 0),
              comment: r.comment,
              isPublished: r.isPublished,
              receivedDate: r.createdAt,
            })),
          }));
          
          // Guardar el estado inicial para detectar cambios
          setInitialProfileData({
            nombre: data.nombre || '',
            bio: data.bio || '',
            avatar: data.avatar || null,
            fondoHeaderImagen: data.fondoHeaderImagen || null,
            fotos: data.fotos || [],
            videos: data.videos || [],
            contactoPublico: { ...contactoMerged },
            redesSociales: data.redesSociales || {},
            actividadesYCultos: data.actividadesYCultos || [],
            tipoEntidadId,
            tipoEntidad: tipoEnt,
            perfilVariante,
            tipoGrupo: data.tipoGrupo || tipoEnt?.nombre || '',
            habitantesMunicipio:
              data.habitantesMunicipio != null ? String(data.habitantesMunicipio) : '',
            fiestasLocales: fiestasRaw.map((f) => ({
              nombre: f?.nombre || '',
              mes: f?.mes != null && f?.mes !== '' ? String(f.mes) : '',
              dia: f?.dia != null && f?.dia !== '' ? String(f.dia) : '',
              fechaInicio: f?.fechaInicio ? String(f.fechaInicio).trim() : '',
              fechaFin: f?.fechaFin ? String(f.fechaFin).trim() : '',
              notas: f?.notas || '',
            })),
            patronDelPueblo: data.patronDelPueblo || '',
            patronaDelPueblo: data.patronaDelPueblo || '',
            culturalAmbito: data.culturalAmbito || '',
            culturalNumeroSocios:
              data.culturalNumeroSocios != null ? String(data.culturalNumeroSocios) : '',
            culturalActividadesDescripcion: data.culturalActividadesDescripcion || '',
            localizacion:
              data.localizacion != null && typeof data.localizacion === 'object' ? data.localizacion : {},
            lat: parseCofradiaLatLngFromApi(data.lat),
            lng: parseCofradiaLatLngFromApi(data.lng),
            cif: data.cif || '',
            personaResponsable: data.personaResponsable || {},
            numeroHermanos: data.numeroHermanos?.toString() || '',
            fechaFundacion: data.fechaFundacion || '',
            patrono: data.patrono || '',
            sedeCanonica: data.sedeCanonica || '',
            horariosCulto: data.horariosCulto || '',
            procesionesAnuales: data.procesionesAnuales || '',
            patrimonioArtistico: data.patrimonioArtistico || '',
          });
          const status = data.verificationStatus?.toLowerCase() || 'not_requested';
          setVerificationStatus(status);

          // Si está pendiente, cargar el código de verificación
          if (status === 'pending') {
            try {
              const verRes = await cofradiasAPI.getVerificationStatus();
              if (verRes.data?.lastRequest?.verificationCode) {
                setVerificationCode(verRes.data.lastRequest.verificationCode);
              }
            } catch (verError) {
              console.error('Error cargando código de verificación:', verError);
            }
          }
        }
      } catch (error) {
        console.error('Error cargando perfil:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, []);

  // Cargar avales otorgados
  useEffect(() => {
    const loadAvales = async () => {
      try {
        setIsLoadingAvales(true);
        const response = await avalesAPI.getOtorgados();
        setAvalesOtorgados(response.data || []);
      } catch (error) {
        console.error('Error cargando avales:', error);
      } finally {
        setIsLoadingAvales(false);
      }
    };

    loadAvales();
  }, []);

  // Buscar bandas para otorgar aval
  const handleSearchBandas = async () => {
    if (!bandaSearchQuery || bandaSearchQuery.length < 2) return;
    
    setIsSearchingBandas(true);
    try {
      const response = await bandasAPI.getAll({ search: bandaSearchQuery, limit: 10 });
      setBandasSearchResults(response.data?.data || []);
    } catch (error) {
      console.error('Error buscando bandas:', error);
      setBandasSearchResults([]);
    } finally {
      setIsSearchingBandas(false);
    }
  };

  // Publicar/despublicar reseña
  const handleTogglePublishReview = async (resenaId) => {
    try {
      await resenasAPI.togglePublish(resenaId);
      
      // Actualizar el estado local
      setProfileData(prev => ({
        ...prev,
        resenas: prev.resenas.map(r =>
          r.id === resenaId ? { ...r, isPublished: !r.isPublished } : r
        ),
      }));
      
      toaster.create({
        title: 'Reseña actualizada',
        description: 'La visibilidad de la reseña se ha actualizado correctamente.',
        type: 'success',
        duration: 3000,
        closable: true,
      });
    } catch (error) {
      console.error('Error al actualizar reseña:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo actualizar la reseña.',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    }
  };

  // Crear aval para una banda
  const handleCreateAval = async () => {
    if (!selectedBandaForAval) return;
    
    setIsCreatingAval(true);
    try {
      await avalesAPI.otorgar(selectedBandaForAval.id, avalTexto);
      // Recargar avales
      const response = await avalesAPI.getOtorgados();
      setAvalesOtorgados(response.data || []);
      // Limpiar formulario
      setSelectedBandaForAval(null);
      setAvalTexto('');
      setBandaSearchQuery('');
      setBandasSearchResults([]);
      // Mostrar mensaje de éxito
      setModalContent({
        title: 'Aval otorgado',
        message: `Has avalado a ${selectedBandaForAval.nombre}. La banda puede aceptar o rechazar tu aval.`,
        type: 'success'
      });
      onSaveModalOpen();
    } catch (error) {
      console.error('Error creando aval:', error);
      setModalContent({
        title: 'Error',
        message: error.response?.data?.message || 'Error al otorgar el aval.',
        type: 'error'
      });
      onErrorModalOpen();
    } finally {
      setIsCreatingAval(false);
    }
  };

  // Revocar aval
  const handleRevocarAval = async (avalId) => {
    try {
      await avalesAPI.revocar(avalId);
      // Recargar avales
      const response = await avalesAPI.getOtorgados();
      setAvalesOtorgados(response.data || []);
    } catch (error) {
      console.error('Error revocando aval:', error);
      setModalContent({
        title: 'Error',
        message: error.response?.data?.message || 'Error al revocar el aval.',
        type: 'error'
      });
      onErrorModalOpen();
    }
  };

  // Detectar cambios sin guardar
  useEffect(() => {
    if (!initialProfileData) return;
    
    const fieldsToCompare = [
      'nombre', 'bio', 'avatar', 'fondoHeaderImagen', 'tipoGrupo', 'tipoEntidadId', 'perfilVariante', 'cif',
      'numeroHermanos', 'fechaFundacion', 'sedeCanonica',
      'horariosCulto', 'procesionesAnuales', 'patrimonioArtistico',
      'habitantesMunicipio', 'patronDelPueblo', 'patronaDelPueblo',
      'culturalAmbito', 'culturalNumeroSocios', 'culturalActividadesDescripcion',
      'lat',
      'lng',
    ];
    
    let hasChanges = false;
    
    // Comparar campos simples
    for (const field of fieldsToCompare) {
      if (profileData[field] !== initialProfileData[field]) {
        hasChanges = true;
        break;
      }
    }
    
    // Comparar arrays y objetos
    if (!hasChanges) {
      hasChanges = JSON.stringify(profileData.fotos) !== JSON.stringify(initialProfileData.fotos) ||
                   JSON.stringify(profileData.videos) !== JSON.stringify(initialProfileData.videos) ||
                   JSON.stringify(profileData.contactoPublico) !== JSON.stringify(initialProfileData.contactoPublico) ||
                   JSON.stringify(profileData.redesSociales) !== JSON.stringify(initialProfileData.redesSociales) ||
                   JSON.stringify(profileData.actividadesYCultos) !== JSON.stringify(initialProfileData.actividadesYCultos) ||
                   JSON.stringify(profileData.localizacion) !== JSON.stringify(initialProfileData.localizacion) ||
                   JSON.stringify(profileData.personaResponsable) !== JSON.stringify(initialProfileData.personaResponsable) ||
                   JSON.stringify(profileData.fiestasLocales || []) !== JSON.stringify(initialProfileData.fiestasLocales || []);
    }
    
    setHasUnsavedChanges(hasChanges);
  }, [profileData, initialProfileData]);

  // Prevenir cierre de pestaña/navegador con cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Función para manejar navegación con cambios sin guardar
  const handleNavigationAttempt = (path) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setIsUnsavedChangesModalOpen(true);
    } else {
      navigate(path);
    }
  };

  // Confirmar navegación descartando cambios
  const confirmNavigation = () => {
    setIsUnsavedChangesModalOpen(false);
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  // Cancelar navegación
  const cancelNavigation = () => {
    setIsUnsavedChangesModalOpen(false);
    setPendingNavigation(null);
  };

  // Modales
  const { open: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
  const { open: isVerificationModalOpen, onOpen: onVerificationModalOpen, onClose: onVerificationModalClose } = useDisclosure();
  const { open: isErrorModalOpen, onOpen: onErrorModalOpen, onClose: onErrorModalClose } = useDisclosure();
  const { open: isDeactivateModalOpen, onOpen: onDeactivateModalOpen, onClose: onDeactivateModalClose } = useDisclosure();
  const { open: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const { open: isUsernameModalOpen, onOpen: onUsernameModalOpen, onClose: onUsernameModalClose } = useDisclosure();
  const { open: isFiestaLocalModalOpen, onOpen: onFiestaLocalModalOpen, onClose: onFiestaLocalModalClose } =
    useDisclosure();
  const [fiestaModalEditIndex, setFiestaModalEditIndex] = useState(null);
  const [fiestaModalDraft, setFiestaModalDraft] = useState({
    nombre: '',
    fechaInicio: '',
    fechaFin: '',
    notas: '',
  });
  
  // Modal para confirmar eliminación de todas las fotos
  const { open: isDeleteAllPhotosModalOpen, onOpen: onDeleteAllPhotosModalOpen, onClose: onDeleteAllPhotosModalClose } = useDisclosure();
  const [isDeletingPhotos, setIsDeletingPhotos] = useState(false);
  
  const [deactivatePassword, setDeactivatePassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'success' // 'success', 'error', 'warning', 'info'
  });

  const bg = useColorModeValue('gray.50', 'gray.900');

  // Sugerencias de actividades y cultos para cofradías
  const actividadesSugeridas = [
    'Semana Santa',
    'Cultos Cuaresmales',
    'Traslados',
    'Vía Crucis',
    'Rosarios',
    'Procesiones Extraordinarias',
    'Besamanos',
    'Besapiés',
    'Triduos',
    'Novenas',
    'Función Principal',
    'Pregón',
    'Conferencias',
    'Exposiciones',
    'Conciertos Sacros',
    'Misas Solemnes',
    'Te Deum',
    'Salves',
    'Quinarios',
    'Septenarios',
    'Octavarios',
    'Festividad del Titular',
    'Cultos Marianos',
    'Adoración Eucarística',
    'Jornadas de Convivencia',
    'Peregrinaciones',
    'Retiros Espirituales',
    'Catequesis',
    'Obras Benéficas',
    'Actividades Culturales'
  ];
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const checklistBg = useColorModeValue('blue.50', 'blue.900');
  const checklistBorderColor = useColorModeValue('blue.200', 'blue.700');
  const checklistTitleColor = useColorModeValue('blue.700', 'blue.200');
  const checklistSubtitleColor = useColorModeValue('blue.600', 'blue.300');
  const checkDoneColor = useColorModeValue('gray.600', 'gray.300');
  const checkTodoColor = useColorModeValue('gray.700', 'gray.200');
  const fiestaListRowBg = useColorModeValue('gray.50', 'gray.900');
  const fiestaEmptyAreaBg = useColorModeValue('purple.50', 'whiteAlpha.50');
  const fiestaEmptyBorderCol = useColorModeValue('purple.200', 'purple.600');
  const reviewCardBg = useColorModeValue('white', 'gray.800');
  const reviewCommentBg = useColorModeValue('gray.50', 'gray.700');

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setProfileData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const deriveMesDiaFromIso = (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return { mes: '', dia: '' };
    const parts = iso.split('-');
    return { mes: String(parseInt(parts[1], 10)), dia: String(parseInt(parts[2], 10)) };
  };

  const openFiestaLocalModalNew = () => {
    setFiestaModalEditIndex(null);
    setFiestaModalDraft({ nombre: '', fechaInicio: '', fechaFin: '', notas: '' });
    onFiestaLocalModalOpen();
  };

  const openFiestaLocalModalEdit = (idx) => {
    const f = (profileData.fiestasLocales || [])[idx];
    if (!f) return;
    setFiestaModalEditIndex(idx);
    setFiestaModalDraft({
      nombre: f.nombre || '',
      fechaInicio: (f.fechaInicio && String(f.fechaInicio).trim()) || '',
      fechaFin: (f.fechaFin && String(f.fechaFin).trim()) || '',
      notas: f.notas || '',
    });
    onFiestaLocalModalOpen();
  };

  const saveFiestaLocalFromModal = () => {
    const nombre = (fiestaModalDraft.nombre || '').trim();
    if (!nombre) {
      showModal('Falta el nombre', 'Indica el nombre de la festividad.', 'error');
      return;
    }
    const fi = (fiestaModalDraft.fechaInicio || '').trim();
    const ffRaw = (fiestaModalDraft.fechaFin || '').trim();
    if (fi && ffRaw && fi > ffRaw) {
      showModal('Fechas incorrectas', 'La fecha de fin debe ser igual o posterior a la de inicio.', 'error');
      return;
    }
    const ff = ffRaw || fi;
    const { mes, dia } = deriveMesDiaFromIso(fi);
    const entry = {
      nombre,
      fechaInicio: fi,
      fechaFin: fi ? ff : '',
      notas: (fiestaModalDraft.notas || '').trim(),
      mes,
      dia,
    };
    if (fiestaModalEditIndex == null) {
      setProfileData((prev) => ({
        ...prev,
        fiestasLocales: [...(prev.fiestasLocales || []), entry],
      }));
    } else {
      setProfileData((prev) => {
        const list = [...(prev.fiestasLocales || [])];
        list[fiestaModalEditIndex] = { ...list[fiestaModalEditIndex], ...entry };
        return { ...prev, fiestasLocales: list };
      });
    }
    onFiestaLocalModalClose();
  };

  const removeFiestaLocal = (index) => {
    setProfileData((prev) => ({
      ...prev,
      fiestasLocales: (prev.fiestasLocales || []).filter((_, i) => i !== index),
    }));
  };

  // Abrir el cropper cuando se selecciona un archivo para avatar
  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tamaño del archivo (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      showModal('Error', 'La imagen es demasiado grande. Máximo 5MB.', 'error');
      return;
    }

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      showModal('Error', 'Por favor selecciona un archivo de imagen válido.', 'error');
      return;
    }

    // Leer el archivo y abrir el cropper
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropperImage(e.target.result);
      setCropperType('avatar');
      setIsCropperOpen(true);
    };
    reader.readAsDataURL(file);
    // Limpiar el input para permitir seleccionar el mismo archivo otra vez
    event.target.value = '';
  };

  // Procesar la imagen recortada del avatar
  const handleAvatarCropComplete = async (croppedImageUrl) => {
    setIsUploading(true);
    try {
      // Convertir la URL del blob a un archivo
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      
      const apiResponse = await cofradiasAPI.uploadAvatar(file);
      if (apiResponse.data?.url) {
        setProfileData(prev => ({
          ...prev,
          avatar: apiResponse.data.url
        }));
        trackAvatarUpload('COFRADIA');
      } else {
        // Fallback a la URL del blob
        setProfileData(prev => ({ ...prev, avatar: croppedImageUrl }));
      }
    } catch (error) {
      // Fallback a la URL del blob
      setProfileData(prev => ({ ...prev, avatar: croppedImageUrl }));
    } finally {
      setIsUploading(false);
    }
  };

  // Abrir el cropper cuando se selecciona un archivo para fondo
  const handleFondoHeaderUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tamaño del archivo (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      showModal('Error', 'La imagen es demasiado grande. Máximo 5MB.', 'error');
      return;
    }

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      showModal('Error', 'Por favor selecciona un archivo de imagen válido.', 'error');
      return;
    }

    // Leer el archivo y abrir el cropper
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropperImage(e.target.result);
      setCropperType('fondo');
      setIsCropperOpen(true);
    };
    reader.readAsDataURL(file);
    // Limpiar el input para permitir seleccionar el mismo archivo otra vez
    event.target.value = '';
  };

  // Procesar la imagen recortada del fondo
  const handleFondoCropComplete = async (croppedImageUrl) => {
    setIsUploading(true);
    try {
      // Convertir la URL del blob a un archivo
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'fondo.jpg', { type: 'image/jpeg' });
      
      const apiResponse = await cofradiasAPI.uploadFondo(file);
      if (apiResponse.data?.url) {
        setProfileData(prev => ({
          ...prev,
          fondoHeaderImagen: apiResponse.data.url
        }));
      } else {
        // Fallback a la URL del blob
        setProfileData(prev => ({ ...prev, fondoHeaderImagen: croppedImageUrl }));
      }
    } catch (error) {
      // Fallback a la URL del blob
      setProfileData(prev => ({ ...prev, fondoHeaderImagen: croppedImageUrl }));
    } finally {
      setIsUploading(false);
    }
  };

  // Manejar el resultado del cropper
  const handleCropComplete = (croppedImageUrl) => {
    if (cropperType === 'avatar') {
      handleAvatarCropComplete(croppedImageUrl);
    } else if (cropperType === 'fondo') {
      handleFondoCropComplete(croppedImageUrl);
    }
    setCropperImage(null);
    setCropperType(null);
  };

  // Editar la imagen actual del avatar (sin subir una nueva)
  const handleEditCurrentAvatar = () => {
    if (profileData.avatar) {
      setCropperImage(profileData.avatar);
      setCropperType('avatar');
      setIsCropperOpen(true);
    }
  };

  // Editar la imagen actual del fondo (sin subir una nueva)
  const handleEditCurrentFondo = () => {
    if (profileData.fondoHeaderImagen) {
      setCropperImage(profileData.fondoHeaderImagen);
      setCropperType('fondo');
      setIsCropperOpen(true);
    }
  };

  // Funciones para drag and drop de galería
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const MAX_GALLERY_PHOTOS = 20;

  const validateFiles = (files) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    const validFiles = [];
    const errors = [];

    // Comprobar límite de galería
    const currentCount = profileData.fotos?.length || 0;
    const remaining = MAX_GALLERY_PHOTOS - currentCount;

    if (remaining <= 0) {
      errors.push(`Has alcanzado el límite de ${MAX_GALLERY_PHOTOS} fotos. Elimina alguna para subir más.`);
      return { validFiles, errors };
    }

    const filesToCheck = Array.from(files).slice(0, remaining);

    if (filesToCheck.length < files.length) {
      errors.push(`Solo se pueden subir ${remaining} foto(s) más (límite: ${MAX_GALLERY_PHOTOS}). Se ignorarán las restantes.`);
    }

    filesToCheck.forEach((file, index) => {
      if (!validTypes.includes(file.type)) {
        errors.push(`Archivo ${index + 1}: Solo se permiten imágenes JPG, PNG o WebP`);
      } else if (file.size > maxSize) {
        errors.push(`Archivo ${index + 1}: El tamaño máximo es 5MB`);
      } else {
        validFiles.push(file);
      }
    });

    return { validFiles, errors };
  };

  const processFiles = async (files) => {
    setIsUploading(true);
    const { validFiles, errors } = validateFiles(files);

    // Si solo hay errores y no hay archivos válidos, mostrar error
    if (validFiles.length === 0) {
      if (errors.length > 0) {
        showModal(
          'Archivos No Válidos',
          errors.join('\n'),
          'error'
        );
      }
      setIsUploading(false);
      return;
    }

    try {
      // Subir fotos al servidor
      const response = await cofradiasAPI.uploadPhotos(validFiles);
      
      if (response.data && response.data.urls) {
        // Las URLs devueltas por el servidor
        const newPhotos = response.data.urls;
        
        setProfileData(prev => ({
          ...prev,
          fotos: [...prev.fotos, ...newPhotos]
        }));

        const currentTotal = (profileData.fotos?.length || 0) + newPhotos.length;
        let message = `Se han subido ${newPhotos.length} foto(s) a tu galería.`;
        if (response.data.warning) {
          message += `\n${response.data.warning}`;
        }
        message += `\n(${currentTotal}/${MAX_GALLERY_PHOTOS} fotos)`;

        showModal(
          'Fotos Subidas',
          message,
          'success'
        );
        trackPhotoUpload('COFRADIA', newPhotos.length);
      }
    } catch (error) {
      console.error('Error subiendo fotos:', error);
      const errorMsg = error.response?.data?.message || 'Hubo un problema al subir las imágenes. Inténtalo de nuevo.';
      showModal(
        'Error al Subir',
        errorMsg,
        'error'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, []);

  const handlePhotoUpload = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const removePhoto = async (photoUrl) => {
    try {
      // Eliminar foto del servidor
      await cofradiasAPI.deletePhoto(photoUrl);
      
      // Actualizar estado local
      setProfileData(prev => ({
        ...prev,
        fotos: prev.fotos.filter(photo => photo !== photoUrl)
      }));
      
      showModal('Foto Eliminada', 'La foto ha sido eliminada correctamente.', 'success');
    } catch (error) {
      console.error('Error eliminando foto:', error);
      showModal('Error', 'No se pudo eliminar la foto. Inténtalo de nuevo.', 'error');
    }
  };

  const calculateProfileCompletion = () => {
    const v = profileData.perfilVariante || COFRADIA_VARIANTE_DEFAULT;
    const checks = cofradiaProfileChecksForVariant(v);
    const n = checks.length;
    if (!n) return 100;
    const completed = checks.filter((c) => c.test(profileData)).length;
    return Math.round((completed / n) * 100);
  };

  /** Obligatorios para guardar: mismos criterios que antes; la validación automática no exige el 100% de todos los campos del perfil */
  const isCofradiaMandatoryCompleteForSave = () =>
    COFRADIA_MANDATORY_PROFILE_CHECKS.every((c) => c.test(profileData));

  const getMissingCofradiaMandatoryLabels = () => {
    const v = profileData.perfilVariante || COFRADIA_VARIANTE_DEFAULT;
    return COFRADIA_MANDATORY_PROFILE_CHECKS.filter((c) => !c.test(profileData)).map((c) =>
      resolveCofradiaCheckLabel(c, v),
    );
  };

  const missingMandatoryLabels = getMissingCofradiaMandatoryLabels();

  const profileCompletionPct = calculateProfileCompletion();
  const showCompletenessSection = profileCompletionPct < 100;
  /** Aviso/código de validación junto a la barra de completitud (aún no validada o pendiente con campos por completar). */
  const integrateValidationWithCompleteness =
    showCompletenessSection &&
    (verificationStatus === 'not_requested' || verificationStatus === 'pending');
  /** Tarjeta de validación en la pestaña: solo si ya está validada, o sin barra de completitud. */
  const showValidationCardInTab =
    verificationStatus === 'verified' ||
    verificationStatus === 'rejected' ||
    ((verificationStatus === 'pending' || verificationStatus === 'not_requested') &&
      !showCompletenessSection);

  const cofradiaVisibilidadCopy = useMemo(
    () => getCofradiaVisibilidadCopy(profileData.perfilVariante),
    [profileData.perfilVariante],
  );

  const cofradiaChecksForVariant = useMemo(
    () => cofradiaProfileChecksForVariant(profileData.perfilVariante),
    [profileData.perfilVariante],
  );

  const cofradiaMandatoryChecks = useMemo(
    () => cofradiaChecksForVariant.filter((c) => c.mandatoryForSave),
    [cofradiaChecksForVariant],
  );

  const cofradiaOptionalPendingSlides = useMemo(() => {
    const v = profileData.perfilVariante || COFRADIA_VARIANTE_DEFAULT;
    return cofradiaChecksForVariant
      .filter((c) => !c.mandatoryForSave && !c.test(profileData))
      .map((c) => {
        const label = resolveCofradiaCheckLabel(c, v);
        return {
          id: `opt-${label}`,
          title: 'Recomendado para tu perfil',
          icon: FaExclamationTriangle,
          content: (
            <Text fontSize="sm" color="orange.700">
              <Text as="span" fontWeight="600">
                {label}
              </Text>
              {' — '}
              no es obligatorio para validar, pero mejora tu visibilidad y posicionamiento en Cadencya.
            </Text>
          ),
        };
      });
  }, [cofradiaChecksForVariant, profileData]);

  const profileTipsSlides = useMemo(() => {
    const slides = [
      {
        id: 'visibility',
        title: 'Recomendación',
        icon: FaLightbulb,
        content:
          'Cuanto más completo y actualizado esté tu perfil, más fácil será que bandas y colaboradores os encuentren para actos y colaboraciones.',
      },
      {
        id: 'photos',
        title: 'Consejos para mejores fotos',
        icon: FaCamera,
        content: (
          <Text fontSize="sm" color="blue.600">
            • Usa imágenes nítidas de sede, actos o patrimonio
            <br />
            • Evita fotos borrosas o con poca luz
            <br />• Máximo 5MB por imagen para una carga rápida
          </Text>
        ),
      },
    ];
    if (showCompletenessSection && verificationStatus !== 'pending') {
      slides.splice(1, 0, {
        id: 'completion',
        title: 'Consejo',
        icon: FaLightbulb,
        content: cofradiaVisibilidadCopy.consejoBody,
      });
    }
    return slides;
  }, [showCompletenessSection, verificationStatus, cofradiaVisibilidadCopy.consejoBody]);

  const bioHistoriaCopy = useMemo(
    () =>
      getCofradiaBioHistoriaCopy({
        perfilVariante: profileData.perfilVariante,
        tipoEntidad: profileData.tipoEntidad,
      }),
    [profileData.perfilVariante, profileData.tipoEntidad],
  );

  const cofradiaPerfilPlaceholders = useMemo(() => {
    const ctx = {
      perfilVariante: profileData.perfilVariante,
      tipoEntidad: profileData.tipoEntidad,
    };
    return {
      email: getCofradiaContactoEmailPlaceholder(ctx),
      redSocialHandle: getCofradiaRedSocialHandlePlaceholder(ctx),
      galeriaConsejoLinea1: getCofradiaGaleriaFotosConsejoLinea1(ctx),
      youtubeTitulo: getCofradiaYoutubeTituloPlaceholder(ctx),
    };
  }, [profileData.perfilVariante, profileData.tipoEntidad]);

  // Función para extraer el ID de un video de YouTube
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };

  const isYouTubeShort = (url) =>
    !!(url && /youtube\.com\/shorts\//.test(url));

  // Función para añadir un video
  const handleAddVideo = () => {
    const videoId = extractYouTubeId(newVideoUrl);
    
    if (!videoId) {
      showModal(
        'URL inválida',
        'Por favor, introduce una URL válida de YouTube.',
        'error'
      );
      return;
    }
    
    const newVideo = {
      titulo: newVideoTitle.trim(),
      url: newVideoUrl.trim(),
    };
    
    setProfileData(prev => ({
      ...prev,
      videos: [...(prev.videos || []), newVideo],
    }));
    
    // Limpiar campos
    setNewVideoTitle('');
    setNewVideoUrl('');
  };

  // Función para eliminar un video
  const handleRemoveVideo = (index) => {
    setProfileData(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index),
    }));
  };

  // Función para eliminar todas las fotos
  const handleDeleteAllPhotos = async () => {
    setIsDeletingPhotos(true);
    try {
      const fotosToDelete = [...profileData.fotos];
      for (const photoUrl of fotosToDelete) {
        try {
          await cofradiasAPI.deletePhoto(photoUrl);
        } catch (error) {
          console.error('Error eliminando foto:', error);
        }
      }
      // Actualizar el estado local
      setProfileData(prev => ({ ...prev, fotos: [] }));
      setInitialProfileData(prev => ({ ...prev, fotos: [] }));
      onDeleteAllPhotosModalClose();
      toaster.create({
        title: 'Fotos eliminadas',
        description: 'Todas las fotos han sido eliminadas correctamente',
        type: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error eliminando fotos:', error);
      toaster.create({
        title: 'Error',
        description: 'Hubo un problema al eliminar las fotos',
        type: 'error',
        duration: 3000,
      });
    } finally {
      setIsDeletingPhotos(false);
    }
  };

  // Función para desactivar cuenta
  const handleDeactivateAccount = async () => {
    if (!deactivatePassword) {
      toaster.create({
        title: 'Error',
        description: 'Debes introducir tu contraseña para confirmar',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    setIsDeactivating(true);
    try {
      await authAPI.deactivateAccount(deactivatePassword);
      onDeactivateModalClose();
      toaster.create({
        title: 'Cuenta desactivada',
        description: 'Tu cuenta ha sido desactivada. Serás redirigido al inicio.',
        type: 'info',
        duration: 3000,
      });
      // Cerrar sesión y redirigir
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error desactivando cuenta:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo desactivar la cuenta',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsDeactivating(false);
      setDeactivatePassword('');
    }
  };

  // Función para exportar datos (GDPR)
  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const response = await authAPI.exportUserData();
      const data = response.data;
      
      // Crear archivo JSON para descargar
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mis-datos-cadencya-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toaster.create({
        title: 'Datos exportados',
        description: 'Tus datos se han descargado correctamente.',
        type: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error exportando datos:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudieron exportar los datos',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Función para eliminar cuenta permanentemente (GDPR)
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toaster.create({
        title: 'Error',
        description: 'Debes introducir tu contraseña para confirmar',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    if (deleteConfirmText !== 'ELIMINAR') {
      toaster.create({
        title: 'Error',
        description: 'Debes escribir ELIMINAR para confirmar',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    setIsDeleting(true);
    try {
      await authAPI.deleteAccountPermanently(deletePassword);
      onDeleteModalClose();
      
      // Cerrar sesión inmediatamente
      logout();
      
      toaster.create({
        title: 'Cuenta eliminada',
        description: 'Tu cuenta y todos tus datos han sido eliminados permanentemente.',
        type: 'info',
        duration: 5000,
      });
      
      // Redirigir a la landing page inmediatamente
      navigate('/');
    } catch (error) {
      console.error('Error eliminando cuenta:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo eliminar la cuenta',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
      setDeletePassword('');
      setDeleteConfirmText('');
    }
  };

  const showModal = (title, message, type = 'success') => {
    setModalContent({ title, message, type });
    if (type === 'error') {
      onErrorModalOpen();
    } else {
      onSaveModalOpen();
    }
  };

  // Función para buscar ubicaciones en tiempo real
  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    setIsSearchingLocation(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=es&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error('Error al buscar ubicación');
      }

      const data = await response.json();
      console.log('🔍 Resultados de búsqueda:', data);

      setLocationSuggestions(data);
      setShowLocationSuggestions(true);
    } catch (error) {
      console.error('❌ Error buscando ubicación:', error);
      setLocationSuggestions([]);
    } finally {
      setIsSearchingLocation(false);
    }
  };

  // Función para seleccionar una ubicación de las sugerencias
  const selectLocation = (location) => {
    const parsed = parseNominatimAddress(location);
    if (parsed.lat == null || parsed.lng == null) {
      toaster.create({
        title: 'Ubicación sin coordenadas',
        description: 'Por favor, introduce una dirección más específica (calle, número, ciudad) para obtener las coordenadas exactas.',
        type: 'warning',
        duration: 5000,
        closable: true,
        position: 'top',
      });
      return;
    }

    setProfileData((prev) => ({
      ...prev,
      localizacion: parsed.localizacion,
      lat: parsed.lat,
      lng: parsed.lng,
    }));

    locationJustSelectedRef.current = true;
    setLocationSearchQuery(location.display_name || parsed.localizacion.calle);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationJustSelectedRef.current) {
        locationJustSelectedRef.current = false;
        return;
      }
      if (locationSearchQuery) {
        searchLocation(locationSearchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [locationSearchQuery]);

  const handleSave = async () => {
    if (missingMandatoryLabels.length > 0) {
      toaster.create({
        title: 'Apartados obligatorios pendientes',
        description: `No se puede guardar hasta completar: ${missingMandatoryLabels.join(', ')}.`,
        type: 'warning',
        duration: 10000,
        closable: true,
        position: 'top',
      });
      return;
    }

    setIsLoading(true);
    try {
      // Preparar datos para la API
      const parseOptInt = (v) => {
        if (v === '' || v == null) return null;
        const n = parseInt(String(v), 10);
        return Number.isFinite(n) ? n : null;
      };
      const dataToUpdate = {
        nombre: profileData.nombre,
        bio: profileData.bio,
        numeroHermanos: profileData.numeroHermanos ? parseInt(profileData.numeroHermanos, 10) : null,
        fechaFundacion: profileData.fechaFundacion,
        patrono: profileData.patrono,
        sedeCanonica: profileData.sedeCanonica,
        horariosCulto: profileData.horariosCulto,
        procesionesAnuales: profileData.procesionesAnuales,
        patrimonioArtistico: profileData.patrimonioArtistico,
        localizacion: profileData.localizacion,
        lat: profileData.lat != null && profileData.lat !== '' ? Number(profileData.lat) : null,
        lng: profileData.lng != null && profileData.lng !== '' ? Number(profileData.lng) : null,
        contactoPublico: profileData.contactoPublico,
        redesSociales: profileData.redesSociales,
        actividadesYCultos: profileData.actividadesYCultos,
        habitantesMunicipio: parseOptInt(profileData.habitantesMunicipio),
        fiestasLocales: (profileData.fiestasLocales || [])
          .filter((f) => f?.nombre?.trim())
          .map((f) => ({
            nombre: f.nombre.trim(),
            mes: parseOptInt(f.mes) ?? undefined,
            dia: parseOptInt(f.dia) ?? undefined,
            fechaInicio: f.fechaInicio?.trim() || undefined,
            fechaFin: f.fechaFin?.trim() || undefined,
            notas: f.notas?.trim() || undefined,
          })),
        patronDelPueblo: profileData.patronDelPueblo || undefined,
        patronaDelPueblo: profileData.patronaDelPueblo || undefined,
        culturalAmbito: profileData.culturalAmbito || undefined,
        culturalNumeroSocios: parseOptInt(profileData.culturalNumeroSocios),
        culturalActividadesDescripcion: profileData.culturalActividadesDescripcion || undefined,
        videos: (profileData.videos || []).map(v => ({
          ...(typeof v.id === 'string' && v.id.length > 10 ? { id: v.id } : {}), // Solo IDs válidos de la BD
          titulo: v.titulo,
          url: v.url,
        })),
        cif: profileData.cif,
        personaResponsable: profileData.personaResponsable,
      };
      
      console.log('Datos a enviar:', dataToUpdate);
      console.log('actividadesYCultos:', profileData.actividadesYCultos);
      console.log('💾 [FRONTEND COFRADIA] Coordenadas a enviar:', {
        lat: dataToUpdate.lat,
        lng: dataToUpdate.lng,
        localizacion: dataToUpdate.localizacion
      });
      
      const result = await updateProfile(dataToUpdate);
      
      if (result.success) {
        // Actualizar el perfil en el contexto con los datos guardados
        const rp = result.profile;
        const te = rp?.tipoEntidad || null;
        setProfileData(prev => ({
          ...prev,
          tipoGrupo: rp?.tipoGrupo || prev.tipoGrupo,
          tipoEntidadId: rp?.tipoEntidadId || te?.id || prev.tipoEntidadId,
          tipoEntidad: te || prev.tipoEntidad,
          perfilVariante: te?.variantePerfil || prev.perfilVariante,
          ...(rp?.localizacion != null && typeof rp.localizacion === 'object'
            ? { localizacion: rp.localizacion }
            : {}),
          lat: parseCofradiaLatLngFromApi(rp?.lat) ?? prev.lat,
          lng: parseCofradiaLatLngFromApi(rp?.lng) ?? prev.lng,
        }));
        
        // Actualizar el estado inicial para que ya no haya "cambios sin guardar"
        setInitialProfileData({
          nombre: profileData.nombre,
          bio: profileData.bio,
          avatar: profileData.avatar,
          fondoHeaderImagen: profileData.fondoHeaderImagen,
          fotos: profileData.fotos,
          videos: profileData.videos,
          contactoPublico: profileData.contactoPublico,
          redesSociales: profileData.redesSociales,
          actividadesYCultos: profileData.actividadesYCultos,
          tipoGrupo: profileData.tipoGrupo,
          tipoEntidadId: profileData.tipoEntidadId,
          tipoEntidad: profileData.tipoEntidad,
          perfilVariante: profileData.perfilVariante,
          habitantesMunicipio: profileData.habitantesMunicipio,
          fiestasLocales: profileData.fiestasLocales || [],
          patronDelPueblo: profileData.patronDelPueblo,
          patronaDelPueblo: profileData.patronaDelPueblo,
          culturalAmbito: profileData.culturalAmbito,
          culturalNumeroSocios: profileData.culturalNumeroSocios,
          culturalActividadesDescripcion: profileData.culturalActividadesDescripcion,
          localizacion:
            rp?.localizacion != null && typeof rp.localizacion === 'object'
              ? rp.localizacion
              : profileData.localizacion,
          lat: parseCofradiaLatLngFromApi(rp?.lat) ?? profileData.lat,
          lng: parseCofradiaLatLngFromApi(rp?.lng) ?? profileData.lng,
          cif: profileData.cif,
          personaResponsable: profileData.personaResponsable,
          numeroHermanos: profileData.numeroHermanos,
          fechaFundacion: profileData.fechaFundacion,
          patrono: profileData.patrono,
          sedeCanonica: profileData.sedeCanonica,
          horariosCulto: profileData.horariosCulto,
          procesionesAnuales: profileData.procesionesAnuales,
          patrimonioArtistico: profileData.patrimonioArtistico,
        });
        setHasUnsavedChanges(false);
        
        // Auto-trigger verification cuando los datos obligatorios para guardar están completos (no exige el 100% de la barra de completitud total)
        if (isCofradiaMandatoryCompleteForSave() && verificationStatus === 'not_requested') {
          try {
            const verResponse = await cofradiasAPI.requestVerification({
              cif: profileData.cif,
              personaResponsable: {
                nombre: profileData.personaResponsable.nombre,
                apellidos: profileData.personaResponsable.apellidos,
                dni: profileData.personaResponsable.dni || '',
                telefono: profileData.personaResponsable.telefono,
                email: profileData.personaResponsable.email,
              },
            });
            setVerificationStatus('pending');
            if (verResponse.data?.verificationCode) {
              setVerificationCode(verResponse.data.verificationCode);
            }
            showModal(
              '¡Listo para validar!',
              'Has completado los datos obligatorios y hemos iniciado automáticamente la validación. Revisa la pestaña de Perfil público para ver tu código de validación y las instrucciones para completar el proceso.',
              'success'
            );
          } catch (verError) {
            console.error('Error auto-requesting verification:', verError);
            showModal(
              '¡Perfil Actualizado!',
              'Tus cambios han sido guardados correctamente.',
              'success'
            );
          }
        } else {
          showModal(
            '¡Perfil Actualizado!',
            'Tus cambios han sido guardados correctamente. Tu información está ahora disponible en la plataforma.',
            'success'
          );
        }
        trackProfileUpdate('COFRADIA', Object.keys(dataToUpdate));
        trackProfileCompletion('COFRADIA', calculateProfileCompletion());
      }

    } catch (error) {
      console.error('Error al guardar el perfil:', error);
      showModal(
        'Error al Guardar',
        error.response?.data?.message || 'No se pudieron guardar los cambios. Por favor, verifica tu conexión e inténtalo de nuevo.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Función para generar URL del mapa
  const generateMapUrl = () => {
    const { calle, numero, ciudad, provincia, codigoPostal, pais } = profileData.localizacion;
    if (!calle || !ciudad || !provincia) return null;
    
    const address = `${calle} ${numero}, ${ciudad}, ${provincia} ${codigoPostal}, ${pais}`.trim();
    const encodedAddress = encodeURIComponent(address);
    
    // Usar Google Maps embed para un diseño más moderno
    return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dYBTTHiOUjdOL0&q=${encodedAddress}`;
  };

  const validateAndShowErrors = () => {
    const requiredFieldsWithNames = [
      { 
        field: profileData.nombre, 
        name: 'Nombre de la cofradía',
        key: 'nombre'
      },
      { 
        field: profileData.personaResponsable.nombre, 
        name: 'Nombre del responsable',
        key: 'personaResponsable.nombre'
      },
      { 
        field: profileData.personaResponsable.apellidos, 
        name: 'Apellidos del responsable',
        key: 'personaResponsable.apellidos'
      },
      { 
        field: profileData.personaResponsable.email, 
        name: 'Email del responsable',
        key: 'personaResponsable.email'
      },
      { 
        field: profileData.localizacion.calle, 
        name: 'Calle',
        key: 'localizacion.calle'
      },
      { 
        field: profileData.localizacion.ciudad, 
        name: 'Ciudad',
        key: 'localizacion.ciudad'
      },
      { 
        field: profileData.localizacion.provincia, 
        name: 'Provincia',
        key: 'localizacion.provincia'
      }
    ];

    const newErrors = {};
    
    requiredFieldsWithNames.forEach(item => {
      if (!item.field || item.field.toString().trim() === '') {
        newErrors[item.key] = `${item.name} es obligatorio`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const navigateToField = (fieldRef, tabIndex) => {
    // Validar formulario y mostrar errores
    validateAndShowErrors();
    
    // Cambiar a la pestaña correcta
    setActiveTab(tabIndex);
    
    // Esperar un momento para que la pestaña se renderice
    setTimeout(() => {
      if (fieldRef.current) {
        fieldRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        fieldRef.current.focus();
        
        // Añadir un efecto visual temporal al campo
        fieldRef.current.style.boxShadow = '0 0 0 3px rgba(245, 101, 101, 0.5)';
        setTimeout(() => {
          if (fieldRef.current) {
            fieldRef.current.style.boxShadow = '';
          }
        }, 2000);
      }
    }, 100);
  };

  const handleRequestVerification = async () => {
    // Validar que los campos requeridos estén completos
    const requiredFieldsWithNames = [
      { 
        field: profileData.nombre, 
        name: 'Nombre de la cofradía',
        ref: nombreRef,
        tab: 0,
        section: 'Perfil público'
      },
      { 
        field: profileData.personaResponsable.nombre, 
        name: 'Nombre del responsable',
        ref: responsableNombreRef,
        tab: 1,
        section: 'Privado'
      },
      { 
        field: profileData.personaResponsable.apellidos, 
        name: 'Apellidos del responsable',
        ref: responsableApellidosRef,
        tab: 1,
        section: 'Privado'
      },
      { 
        field: profileData.personaResponsable.email, 
        name: 'Email del responsable',
        ref: responsableEmailRef,
        tab: 1,
        section: 'Privado'
      },
      { 
        field: profileData.localizacion.calle, 
        name: 'Calle',
        ref: calleRef,
        tab: 0,
        section: 'Perfil público'
      },
      { 
        field: profileData.localizacion.ciudad, 
        name: 'Ciudad',
        ref: ciudadRef,
        tab: 0,
        section: 'Perfil público'
      },
      { 
        field: profileData.localizacion.provincia, 
        name: 'Provincia',
        ref: provinciaRef,
        tab: 0,
        section: 'Perfil público'
      }
    ];

    const missingFields = requiredFieldsWithNames.filter(
      item => !item.field || item.field.toString().trim() === ''
    );
    
    if (missingFields.length > 0) {
      setModalContent({
        title: 'Campos obligatorios faltantes',
        message: 'Completa los siguientes campos para solicitar la validación:',
        type: 'validation',
        missingFields: missingFields
      });
      onErrorModalOpen();
      return;
    }

    setIsLoading(true);
    try {
      // Llamar al API de verificación
      const response = await cofradiasAPI.requestVerification({
        cif: profileData.cif,
        personaResponsable: {
          nombre: profileData.personaResponsable.nombre,
          apellidos: profileData.personaResponsable.apellidos,
          dni: profileData.personaResponsable.dni || '',
          telefono: profileData.personaResponsable.telefono,
          email: profileData.personaResponsable.email,
        },
      });
      
      setVerificationStatus('pending');
      if (response.data?.verificationCode) {
        setVerificationCode(response.data.verificationCode);
      }
      console.log('Solicitud de verificación enviada para:', profileData.nombre);
      
      setModalContent({
        title: '¡Solicitud de validación enviada!',
        message: 'Se ha generado tu código de validación. Envíalo por mensaje directo a cualquiera de nuestros perfiles en redes sociales para completar el proceso.',
        type: 'success'
      });
      onVerificationModalOpen();
      
    } catch (error) {
      console.error('Error al solicitar verificación:', error);
      const errorMessage = error.response?.data?.message || 'No se pudo enviar la solicitud de validación. Por favor, inténtalo de nuevo más tarde.';
      showModal(
        'Error en la Validación',
        errorMessage,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeUsername = async () => {
    if (!newUsername.trim()) return;
    setIsChangingUsername(true);
    try {
      const response = await authAPI.changeUsername(newUsername);
      toaster.create({
        title: 'Username actualizado',
        description: `Tu nuevo usuario es @${response.data.username}. Te quedan ${response.data.changesRemaining} cambios este año.`,
        type: 'success',
        duration: 5000,
        closable: true,
      });
      onUsernameModalClose();
      window.location.reload();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'No se pudo cambiar el nombre de usuario';
      toaster.create({
        title: 'Error',
        description: errorMsg,
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsChangingUsername(false);
    }
  };

  const getVerificationBadge = () => {
    switch (verificationStatus) {
      case 'pending':
        return { colorScheme: 'orange', text: 'Validación Pendiente', icon: FaClock };
      case 'verified':
        return { colorScheme: 'green', text: 'Validado', icon: FaCheckCircle };
      case 'rejected':
        return { colorScheme: 'red', text: 'Validación Rechazada', icon: FaExclamationTriangle };
      default:
        return { colorScheme: 'gray', text: 'Sin Validar', icon: FaShieldAlt };
    }
  };

  const renderVerificationStatusBadge = () => {
    if (verificationStatus === 'not_requested') return null;
    return (
      <Badge
        colorPalette={verificationStatus === 'pending' ? 'orange' : verificationStatus === 'verified' ? 'green' : 'red'}
        px={3}
        py={1}
        rounded="full"
        fontSize="xs"
      >
        <HStack gap={1}>
          <Icon
            as={
              verificationStatus === 'pending'
                ? FaClock
                : verificationStatus === 'verified'
                  ? FaCheckCircle
                  : FaExclamationTriangle
            }
            w={3}
            h={3}
          />
          <Text>
            {verificationStatus === 'pending'
              ? 'Pendiente'
              : verificationStatus === 'verified'
                ? 'Validado'
                : 'Validación rechazada'}
          </Text>
        </HStack>
      </Badge>
    );
  };

  const renderVerificationNotRequestedAlert = () => (
    <Alert.Root status="info" rounded="lg">
      <Alert.Indicator />
      <VStack align="start" gap={1}>
        <Text fontSize="sm" fontWeight="medium">
          {cofradiaVisibilidadCopy.validacionTitle}
        </Text>
        <Text fontSize="xs">
          {cofradiaVisibilidadCopy.validacionBody}
        </Text>
      </VStack>
    </Alert.Root>
  );

  const renderVerificationPendingContent = () => (
    <VStack gap={4} w="full">
      <Box
        w="full"
        bg="purple.50"
        border="2px solid"
        borderColor="purple.300"
        rounded="xl"
        p={6}
      >
        <Text
          fontSize={{ base: '2xl', sm: '3xl', md: '4xl' }}
          fontWeight="bold"
          color="purple.600"
          letterSpacing={{ base: '0.12em', md: '0.3em' }}
          fontFamily="mono"
          textAlign="center"
          wordBreak="break-all"
        >
          {verificationCode || '------'}
        </Text>
        <HStack mt={3} mb={1} justify="center">
          <Badge colorPalette="purple" fontSize="xs" px={2} py={1} rounded="full">
            Paso 1
          </Badge>
          <Text fontSize="sm" fontWeight="600" color="purple.700">
            Copia tu código de validación
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
          Este código es único para tu solicitud
        </Text>
      </Box>

      <Box w="full" bg="white" border="1px solid" borderColor="gray.200" rounded="xl" p={4}>
        <HStack mb={3}>
          <Badge colorPalette="purple" fontSize="xs" px={2} py={1} rounded="full">
            Paso 2
          </Badge>
          <Text fontSize="sm" fontWeight="600">
            Envíalo por mensaje directo
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={3}>
          Abre cualquiera de nuestros perfiles y envíanos un DM con el código:
        </Text>
        <VStack gap={2} align="stretch">
          <HStack
            as="a"
            href="https://www.instagram.com/cadencya_app/"
            target="_blank"
            gap={3}
            p={2}
            rounded="lg"
            _hover={{ bg: 'gray.50' }}
            cursor="pointer"
          >
            <Icon as={FaInstagram} color="pink.500" w={5} h={5} />
            <Text fontSize="sm" color="purple.600" fontWeight="medium">
              @cadencya_app
            </Text>
            <Text fontSize="xs" color="gray.500">
              Instagram
            </Text>
          </HStack>
          <HStack
            as="a"
            href="https://x.com/cadencya_app"
            target="_blank"
            gap={3}
            p={2}
            rounded="lg"
            _hover={{ bg: 'gray.50' }}
            cursor="pointer"
          >
            <Icon as={FaXTwitter} color="gray.800" w={5} h={5} />
            <Text fontSize="sm" color="purple.600" fontWeight="medium">
              @cadencya_app
            </Text>
            <Text fontSize="xs" color="gray.500">
              X
            </Text>
          </HStack>
          <HStack
            as="a"
            href="https://www.tiktok.com/@cadencya_app"
            target="_blank"
            gap={3}
            p={2}
            rounded="lg"
            _hover={{ bg: 'gray.50' }}
            cursor="pointer"
          >
            <Icon as={FaTiktok} color="gray.800" w={5} h={5} />
            <Text fontSize="sm" color="purple.600" fontWeight="medium">
              @cadencya_app
            </Text>
            <Text fontSize="xs" color="gray.500">
              TikTok
            </Text>
          </HStack>
        </VStack>
      </Box>

      <Box w="full" bg="white" border="1px solid" borderColor="gray.200" rounded="xl" p={4}>
        <HStack mb={2}>
          <Badge colorPalette="purple" fontSize="xs" px={2} py={1} rounded="full">
            Paso 3
          </Badge>
          <Text fontSize="sm" fontWeight="600">
            Espera nuestra confirmación
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          Revisaremos tu mensaje y validaremos tu perfil. Recibirás una notificación y un email cuando esté listo.
        </Text>
      </Box>

      <Alert.Root status="warning" rounded="lg" size="sm" bg="orange.50" borderColor="orange.200" borderWidth="1px">
        <Alert.Indicator color="orange.500" />
        <VStack align="start" gap={1}>
          <Text fontSize="sm" fontWeight="bold" color="orange.700">
            Atención: tu usuario en Cadencya
          </Text>
          <Text fontSize="xs" color="orange.800">
            El nombre de usuario de la red social desde la que nos envíes el código se convertirá en tu nombre de usuario en Cadencya (por ejemplo, @tu_cofradia).
            Asegúrate de enviarlo desde la cuenta oficial de tu cofradía, con datos y actividad reales. No uses una cuenta vacía o recién creada.
          </Text>
        </VStack>
      </Alert.Root>

      <Alert.Root status="info" rounded="lg" size="sm">
        <Alert.Indicator />
        <VStack align="start" gap={1}>
          <Text fontSize="xs" fontWeight="medium">
            Perfil bloqueado temporalmente
          </Text>
          <Text fontSize="xs">
            Mientras tu validación está en proceso, no es posible modificar los datos del perfil. Hasta que tu perfil no sea validado por el equipo de Cadencya en las próximas 48 horas, no serás visible para el resto de usuarios en el apartado de Comunidad.
            Este proceso es para garantizar que todos los usuarios de Cadencya sean reales.
          </Text>
        </VStack>
      </Alert.Root>
    </VStack>
  );

  const renderVerificationVerifiedContent = () => (
    <VStack gap={3} w="full">
      <Alert.Root status="success" rounded="lg" size="sm">
        <Alert.Indicator />
        <VStack align="start" gap={1}>
          <Text fontSize="sm" fontWeight="medium">
            ¡Perfil Validado!
          </Text>
          <Text fontSize="xs">
            Tu cofradía tiene ahora el sello de validación y tu perfil es visible para otros usuarios.
            Esto aumenta tu credibilidad en la plataforma.
          </Text>
        </VStack>
      </Alert.Root>

      {user?.username && (
        <Box w="full" p={4} bg="purple.50" rounded="xl" border="1px solid" borderColor="purple.200">
          <HStack justify="space-between" align="center">
            <VStack align="start" gap={0}>
              <Text fontSize="xs" color="gray.500">
                Tu usuario en Cadencya
              </Text>
              <Text fontSize="md" fontWeight="bold" color="purple.600">
                @{user.username}
              </Text>
            </VStack>
            <Button
              size="xs"
              variant="outline"
              colorPalette="purple"
              onClick={() => {
                setNewUsername(user.username || '');
                onUsernameModalOpen();
              }}
            >
              Cambiar
            </Button>
          </HStack>
          <Text fontSize="xs" color="gray.400" mt={1}>
            Puedes cambiarlo {Math.max(0, 2 - (user.usernameChangesThisYear || 0))}{' '}
            {Math.max(0, 2 - (user.usernameChangesThisYear || 0)) === 1 ? 'vez' : 'veces'} más este año
          </Text>
        </Box>
      )}
    </VStack>
  );

  return (
    <AppLayout 
      userType={userType} 
      userName={getPerfilDisplayName(profileData, 'cofradia')}
      onNavigate={handleNavigationAttempt}
    >
      <Box bg={bg} py={{ base: 4, md: 8 }}>
        <Container maxW={{ base: "full", md: "6xl" }} px={{ base: 4, md: 6 }}>
        {/* Header */}
        <VStack gap={{ base: 4, md: 6 }} mb={{ base: 6, md: 8 }}>
          <HStack gap={4} w="full" justify="space-between" align="center">
            <VStack align="start" gap={1}>
              <HStack>
                <Icon
                  as={getCofradiaPlaceholderIcon(profileData.perfilVariante)}
                  color="purple.600"
                  w={6}
                  h={6}
                />
                <Heading size={{ base: "lg", md: "xl" }}>Mi perfil</Heading>
              </HStack>
              <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                Gestiona la información de tu perfil
              </Text>
            </VStack>
            
            <HStack gap={2} flexWrap="wrap" justify="flex-end">
              <ShareProfileButton shareUrl={publicProfileShareUrl} />
              <Button
                colorPalette="purple"
                variant="outline"
                size={{ base: "sm", md: "md" }}
                rounded="full"
                onClick={() => setShowPublicPreview(true)}><Icon as={FaEye} />Previsualizar perfil público
                              </Button>
            </HStack>
          </HStack>

          {/* Completitud (+ validación integrada si aún no está validada / pendiente con campos por completar) */}
          {showCompletenessSection && (
            <Box
              w="full"
              data-tour="completitud"
              p={4}
              bg={checklistBg}
              rounded="xl"
              border="1px solid"
              borderColor={
                integrateValidationWithCompleteness && verificationStatus === 'pending'
                  ? 'orange.300'
                  : checklistBorderColor
              }
            >
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color={checklistTitleColor} fontWeight="600">
                  Completitud del perfil
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="purple.600">
                  {profileCompletionPct}%
                </Text>
              </HStack>
              <Progress.Root
                value={profileCompletionPct}
                colorPalette="purple"
                size="sm"
                rounded="full">
                <Progress.Track>
                  <Progress.Range />
                </Progress.Track>
              </Progress.Root>

              {integrateValidationWithCompleteness ? (
                <Box mt={4}>
                  <HStack justify="space-between" align="center" mb={3}>
                    <Heading size="sm">
                      <Icon as={FaShieldAlt} mr={2} color="purple.600" />
                      Estado de validación
                    </Heading>
                    {renderVerificationStatusBadge()}
                  </HStack>
                  {verificationStatus === 'not_requested'
                    ? renderVerificationNotRequestedAlert()
                    : renderVerificationPendingContent()}
                </Box>
              ) : null}

              {/* Checklist: solo obligatorios para validar (oculto si ya validado) */}
              {verificationStatus !== 'verified' ? (
                <Box
                  mt={4}
                  pt={integrateValidationWithCompleteness ? 4 : 0}
                  borderTopWidth={integrateValidationWithCompleteness ? '1px' : 0}
                  borderColor={checklistBorderColor}
                >
                  {/* Título/subtítulo: no duplicar el disclaimer cuando aún no hay código */}
                  {verificationStatus !== 'not_requested' ? (
                    <HStack align="flex-start" gap={3} mb={3}>
                      <Icon as={FaInfoCircle} color="blue.500" mt={0.5} flexShrink={0} />
                      <VStack align="start" gap={1} flex={1}>
                        <Text fontSize="sm" fontWeight="600" color={checklistTitleColor}>
                          Campos obligatorios para validar
                        </Text>
                        <Text fontSize="xs" color={checklistSubtitleColor}>
                          Debes completarlos para poder guardar e iniciar la validación. El porcentaje de arriba también incluye otros datos recomendados que mejoran tu visibilidad.
                        </Text>
                      </VStack>
                    </HStack>
                  ) : null}
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={2} columnGap={6}>
                    {cofradiaMandatoryChecks.map((check) => {
                      const done = check.test(profileData);
                      const rowLabel = resolveCofradiaCheckLabel(
                        check,
                        profileData.perfilVariante || COFRADIA_VARIANTE_DEFAULT,
                      );
                      return (
                        <HStack
                          key={rowLabel + (check.variantsOnly?.join?.(',') || '')}
                          gap={3}
                          align="flex-start"
                        >
                          <Box
                            w="20px"
                            h="20px"
                            mt="2px"
                            rounded="full"
                            border="2px solid"
                            borderColor={done ? 'green.500' : 'gray.300'}
                            bg={done ? 'green.500' : 'transparent'}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                          >
                            {done && (
                              <Icon color="white" boxSize={3} asChild>
                                <FaCheck />
                              </Icon>
                            )}
                          </Box>
                          <Text
                            fontSize="sm"
                            color={done ? checkDoneColor : checkTodoColor}
                            textDecoration={done ? 'line-through' : 'none'}
                          >
                            {rowLabel}
                          </Text>
                        </HStack>
                      );
                    })}
                  </SimpleGrid>
                </Box>
              ) : null}

              {cofradiaOptionalPendingSlides.length > 0 ? (
                verificationStatus === 'verified' ? (
                  <Accordion.Root collapsible mt={4} defaultValue={[]}>
                    <Accordion.Item
                      border="1px solid"
                      borderColor={checklistBorderColor}
                      rounded="lg"
                      overflow="hidden"
                      value='item-0'>
                      <Accordion.ItemTrigger py={3} px={3} _expanded={{ bg: checklistBg }}>
                        <Box flex="1" textAlign="left">
                          <Text fontSize="sm" fontWeight="600" color={checklistTitleColor}>
                            Campos recomendados pendientes ({cofradiaOptionalPendingSlides.length})
                          </Text>
                          <Text fontSize="xs" color={checklistSubtitleColor}>
                            No son obligatorios, pero mejoran tu visibilidad en Cadencya.
                          </Text>
                        </Box>
                        <Accordion.ItemIndicator />
                      </Accordion.ItemTrigger>
                      <Accordion.ItemContent pb={3} pt={0} px={3} animation='none'><Accordion.ItemBody>
                          <ProfileTipsCarousel
                            status="warning"
                            mb={0}
                            slides={cofradiaOptionalPendingSlides}
                            aria-label="Campos recomendados del perfil"
                          />
                        </Accordion.ItemBody></Accordion.ItemContent>
                    </Accordion.Item>
                  </Accordion.Root>
                ) : (
                  <Box mt={4}>
                    <ProfileTipsCarousel
                      status="warning"
                      mb={0}
                      slides={cofradiaOptionalPendingSlides}
                      aria-label="Campos recomendados del perfil"
                    />
                  </Box>
                )
              ) : null}
            </Box>
          )}
        </VStack>

        {/* Tabs */}
        <Tabs.Root lazyMount unmountOnExit value={tabsControlledValue(activeTab)} onValueChange={tabsOnValueChange((index) => {
          setActiveTab(index);
          // Limpiar errores al cambiar de pestaña para mejor UX
          if (Object.keys(errors).length > 0) {
            setErrors({});
          }
        })} variant='subtle' colorPalette="purple" size={{ base: "md", md: "lg" }}>
          <Tabs.List gap={2} bg="gray.100" p={2} rounded="full">
            <Tab value="0" fontSize={{ base: "sm", md: "md" }} px={{ base: 4, md: 6 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaUser} mr={2} />
              Perfil público
            </Tab>
            <Tab value="1" fontSize={{ base: "sm", md: "md" }} px={{ base: 4, md: 6 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaHandshake} mr={2} />
              Avales
            </Tab>
            <Tab value="2" fontSize={{ base: "sm", md: "md" }} px={{ base: 4, md: 6 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaComments} mr={2} />
              Reseñas
            </Tab>
            <Tab value="3" fontSize={{ base: "sm", md: "md" }} px={{ base: 4, md: 6 }} py={3} fontWeight="600" rounded="full">
              <Icon as={FaShieldAlt} mr={2} />
              Privado
            </Tab>
          </Tabs.List>

          <TabPanels>
            {/* Perfil público */}
            <TabPanel value="0" px={0} w="full">
              <VStack gap={{ base: 6, md: 8 }} w="full" align="stretch">
              {/* Card de Estado de Validación (solo si ya validó, o sin barra de completitud) */}
              {showValidationCardInTab ? (
              <Card.Root
                bg={cardBg}
                shadow="lg"
                rounded="2xl"
                w="full"
                border="2px solid"
                borderColor={
                  verificationStatus === 'verified'
                    ? 'green.300'
                    : verificationStatus === 'pending'
                    ? 'orange.300'
                    : 'gray.200'
                }
              >
                <Card.Header>
                  <HStack justify="space-between" align="center">
                    <Heading size="md">
                      <Icon as={FaShieldAlt} mr={2} color={
                        verificationStatus === 'verified'
                          ? 'green.500'
                          : verificationStatus === 'pending'
                          ? 'orange.500'
                          : 'purple.600'
                      } />
                      Estado de validación
                    </Heading>
                    {renderVerificationStatusBadge()}
                  </HStack>
                </Card.Header>
                <Card.Body pt={0}>
                  {verificationStatus === 'not_requested' && renderVerificationNotRequestedAlert()}
                  {verificationStatus === 'pending' && renderVerificationPendingContent()}
                  {verificationStatus === 'verified' && renderVerificationVerifiedContent()}
                </Card.Body>
              </Card.Root>
              ) : null}

              <SimpleGrid w="full" columns={{ base: 1, lg: 2 }} gap={{ base: 6, md: 8 }}>
                {/* Información Básica */}
                <VStack gap={{ base: 6, md: 8 }}>
                  <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                    <Card.Header>
                      <Heading size="md">
                        <Icon
                          as={getCofradiaPlaceholderIcon(profileData.perfilVariante)}
                          mr={2}
                          color="purple.600"
                        />
                        Información básica
                      </Heading>
                    </Card.Header>
                    <Card.Body>
                      <VStack gap={4}>
                        {/* Avatar */}
                        <VStack gap={3}>
                          <Avatar.Root size="2xl" bg="purple.100" color="purple.600"><Avatar.Fallback>{<Icon
                                as={getCofradiaPlaceholderIcon(profileData.perfilVariante)}
                                w={12}
                                h={12}
                              />}</Avatar.Fallback><Avatar.Image src={resolveUploadsUrl(profileData.avatar)} /></Avatar.Root>
                          <VStack gap={1}>
                            <HStack gap={2}>
                              <Button
                                size="sm"
                                variant="outline"
                                colorPalette="purple"
                                onClick={() => fileInputRef.current?.click()}><Icon as={FaCamera} />{profileData.avatar ? 'Cambiar' : 'Subir Logo'}</Button>
                              {profileData.avatar && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  colorPalette="purple"
                                  onClick={handleEditCurrentAvatar}><Icon as={FaCrop} />Editar
                                                                  </Button>
                              )}
                            </HStack>
                            <input
                              ref={fileInputRef}
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleAvatarChange}
                            />
                            <Text fontSize="xs" color={textColor} textAlign="center">
                              JPG, PNG. Máximo 5MB.
                            </Text>
                          </VStack>
                        </VStack>

                        {/* Imagen de Fondo del Header */}
                        <VStack gap={4} w="full">
                          <Text fontWeight="medium" alignSelf="start">Imagen de Fondo del Perfil</Text>
                          <Box
                            w="full"
                            h="120px"
                            bg="gray.100"
                            rounded="lg"
                            border="2px dashed"
                            borderColor="gray.300"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            position="relative"
                            overflow="hidden"
                            style={profileData.fondoHeaderImagen ? {
                              backgroundImage: `url(${resolveUploadsUrl(profileData.fondoHeaderImagen)})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            } : {}}
                          >
                            {!profileData.fondoHeaderImagen ? (
                              <VStack gap={2} color="gray.500">
                                <Icon as={FaCamera} w={8} h={8} />
                                <Text fontSize="sm" textAlign="center">
                                  Sube una imagen de fondo
                                </Text>
                              </VStack>
                            ) : (
                              <Box
                                position="absolute"
                                top="2"
                                right="2"
                                bg="blackAlpha.700"
                                rounded="full"
                                p={2}
                              >
                                <Icon as={FaCamera} color="white" w={4} h={4} />
                              </Box>
                            )}
                          </Box>
                          <HStack gap={3} w="full" flexWrap="wrap" justifyContent="center">
                            <Button
                              size="sm"
                              onClick={() => document.getElementById('fondo-header-input-cofradia').click()}
                              variant="outline"><Icon as={FaCamera} />{profileData.fondoHeaderImagen ? 'Cambiar' : 'Subir Fondo'}</Button>
                            {profileData.fondoHeaderImagen && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={handleEditCurrentFondo}
                                  variant="outline"
                                  colorPalette="purple"><Icon as={FaCrop} />Editar
                                                                  </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleInputChange('fondoHeaderImagen', null)}
                                  variant="outline"
                                  colorPalette="red"><Icon as={FaTimes} />Quitar
                                                                  </Button>
                              </>
                            )}
                          </HStack>
                          <input
                            id="fondo-header-input-cofradia"
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleFondoHeaderUpload}
                          />
                          <Text fontSize="xs" color={textColor} textAlign="center">
                            JPG, PNG. Máximo 5MB. Esta imagen aparecerá como fondo en tu perfil público.
                          </Text>
                        </VStack>

                        {/* Nombre */}
                        <Field.Root  invalid={errors.nombre}>
                          <Field.Label>Nombre de la entidad</Field.Label>
                          <Input
                            ref={nombreRef}
                            value={profileData.nombre}
                            onChange={(e) => {
                              handleInputChange('nombre', e.target.value);
                              if (errors.nombre && e.target.value.trim()) {
                                setErrors(prev => ({ ...prev, nombre: undefined }));
                              }
                            }}
                            placeholder="Nombre de tu cofradía"
                            maxLength={40}
                          />
                          <Field.HelperText color={profileData.nombre?.length >= 300 ? 'red.500' : 'gray.500'}>
                            {profileData.nombre?.length || 0}/300 caracteres
                          </Field.HelperText>
                          <Field.ErrorText>{errors.nombre}</Field.ErrorText>
                        </Field.Root>

                        {/* Tipo de entidad (solo lectura; se fija en el registro) */}
                        <Field.Root>
                          <Field.Label>Tipo de entidad</Field.Label>
                          <Input
                            readOnly
                            value={
                              profileData.tipoEntidad?.nombre || profileData.tipoGrupo || '—'
                            }
                            bg={useColorModeValue('gray.50', 'gray.700')}
                            cursor="not-allowed"
                          />
                          <Alert.Root status="info" variant="subtle" rounded="md" mt={2}>
                            <Alert.Indicator />
                            <Box>
                              <Alert.Title fontSize="sm">No editable</Alert.Title>
                              <Alert.Description fontSize="xs">
                                El tipo de entidad se eligió al crear la cuenta y no se puede cambiar. Define la
                                variante de perfil (religioso, ayuntamiento, asociación cultural, fundación u otro).
                              </Alert.Description>
                            </Box>
                          </Alert.Root>
                        </Field.Root>

                        {/* Bio */}
                        <Field.Root>
                          <Field.Label>{bioHistoriaCopy.formLabel}</Field.Label>
                          <Textarea
                            value={profileData.bio}
                            onChange={(e) => handleInputChange('bio', e.target.value)}
                            placeholder={bioHistoriaCopy.placeholder}
                            rows={4}
                          />
                          <Field.HelperText>{bioHistoriaCopy.helperText}</Field.HelperText>
                        </Field.Root>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </VStack>

                {/* Información Específica de Cofradía */}
                <VStack gap={{ base: 6, md: 8 }}>
                  {profileData.perfilVariante === 'RELIGIOSO' && (
                  <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                    <Card.Header>
                                              <Heading size="md">
                          <Icon as={FaGavel} mr={2} color="purple.600" />
                          Información religiosa
                        </Heading>
                    </Card.Header>
                    <Card.Body>
                      <VStack gap={4}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
                          <Field.Root>
                            <Field.Label>Año de fundación</Field.Label>
                            <NumberInput.Root
                              value={profileData.fechaFundacion == null || profileData.fechaFundacion === '' ? '' : String(profileData.fechaFundacion)}
                              onValueChange={(details) =>
                                handleInputChange(
                                  'fechaFundacion',
                                  details && typeof details === 'object' && 'value' in details ? details.value : (details ?? ''),
                                )
                              }
                              min={1000}
                              max={new Date().getFullYear()}
                            >
                              <NumberInput.Input placeholder="Ej: 1950" />
                              <NumberInput.Control>
                                <NumberInput.IncrementTrigger />
                                <NumberInput.DecrementTrigger />
                              </NumberInput.Control>
                            </NumberInput.Root>
                          </Field.Root>
                        </SimpleGrid>

                        <Field.Root>
                          <Field.Label>Sede canónica</Field.Label>
                          <Input
                            value={profileData.sedeCanonica}
                            onChange={(e) => handleInputChange('sedeCanonica', e.target.value)}
                            placeholder="Iglesia o parroquia donde tiene sede"
                          />
                        </Field.Root>

                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
                          <Field.Root>
                            <Field.Label>Número de hermanos</Field.Label>
                            <NumberInput.Root
                              value={profileData.numeroHermanos == null || profileData.numeroHermanos === '' ? '' : String(profileData.numeroHermanos)}
                              onValueChange={(details) =>
                                handleInputChange(
                                  'numeroHermanos',
                                  details && typeof details === 'object' && 'value' in details ? details.value : (details ?? ''),
                                )
                              }
                              min={0}
                            >
                              <NumberInput.Input placeholder="Número aproximado" />
                              <NumberInput.Control>
                                <NumberInput.IncrementTrigger />
                                <NumberInput.DecrementTrigger />
                              </NumberInput.Control>
                            </NumberInput.Root>
                          </Field.Root>
                          <Field.Root>
                            <Field.Label>Procesiones anuales</Field.Label>
                            <Input
                              value={profileData.procesionesAnuales}
                              onChange={(e) => handleInputChange('procesionesAnuales', e.target.value)}
                              placeholder="Ej: 3 (Semana Santa, Corpus, etc.)"
                            />
                          </Field.Root>
                        </SimpleGrid>

                        <Field.Root>
                          <Field.Label>Horarios de culto</Field.Label>
                          <Textarea
                            value={profileData.horariosCulto}
                            onChange={(e) => handleInputChange('horariosCulto', e.target.value)}
                            placeholder="Describe los horarios habituales de culto y ceremonias"
                            rows={3}
                          />
                        </Field.Root>

                        <Field.Root>
                          <Field.Label>Patrimonio artístico</Field.Label>
                          <Textarea
                            value={profileData.patrimonioArtistico}
                            onChange={(e) => handleInputChange('patrimonioArtistico', e.target.value)}
                            placeholder="Describe los pasos, imágenes, enseres y patrimonio artístico"
                            rows={3}
                          />
                        </Field.Root>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                  )}

                  {profileData.perfilVariante === 'AYUNTAMIENTO' && (
                    <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                      <Card.Header>
                        <Heading size="md">
                          <Icon as={FaBuilding} mr={2} color="purple.600" />
                          Municipio
                        </Heading>
                        <Text fontSize="sm" color={textColor}>
                          Datos propios del perfil de ayuntamiento.
                        </Text>
                      </Card.Header>
                      <Card.Body>
                        <VStack gap={4} align="stretch">
                          <Field.Root>
                            <Field.Label>Habitantes (aprox.)</Field.Label>
                            <NumberInput.Root
                              value={profileData.habitantesMunicipio == null || profileData.habitantesMunicipio === '' ? '' : String(profileData.habitantesMunicipio)}
                              onValueChange={(details) =>
                                handleInputChange(
                                  'habitantesMunicipio',
                                  details && typeof details === 'object' && 'value' in details ? details.value : (details ?? ''),
                                )
                              }
                              min={0}
                            >
                              <NumberInput.Input placeholder="Ej: 12000" />
                              <NumberInput.Control>
                                <NumberInput.IncrementTrigger />
                                <NumberInput.DecrementTrigger />
                              </NumberInput.Control>
                            </NumberInput.Root>
                          </Field.Root>
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
                            <Field.Root>
                              <Field.Label>Patrón del pueblo</Field.Label>
                              <Input
                                value={profileData.patronDelPueblo}
                                onChange={(e) => handleInputChange('patronDelPueblo', e.target.value)}
                                placeholder="Titular o patrono litúrgico del municipio"
                              />
                            </Field.Root>
                            <Field.Root>
                              <Field.Label>Patrona del pueblo</Field.Label>
                              <Input
                                value={profileData.patronaDelPueblo}
                                onChange={(e) => handleInputChange('patronaDelPueblo', e.target.value)}
                                placeholder="Si aplica"
                              />
                            </Field.Root>
                          </SimpleGrid>
                          <Box w="full">
                            <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                              <Field.Root>
                                <Field.Label m={0}>Fiestas y ferias locales</Field.Label>
                                <Text fontSize="xs" color={textColor}>
                                  Indica el nombre, el periodo (inicio y fin) y notas opcionales.
                                </Text>
                              </Field.Root>
                              <Button size="sm" colorPalette="purple" onClick={openFiestaLocalModalNew}><Icon as={FaPlus} />Añadir fiesta
                                                              </Button>
                            </HStack>
                            {(profileData.fiestasLocales || []).length === 0 ? (
                              <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                py={10}
                                px={4}
                                rounded="xl"
                                borderWidth="2px"
                                borderStyle="dashed"
                                borderColor={fiestaEmptyBorderCol}
                                bg={fiestaEmptyAreaBg}
                              >
                                <Icon as={FaCalendarAlt} boxSize={10} color="purple.400" mb={3} />
                                <Text fontSize="sm" fontWeight="medium" color="purple.800" textAlign="center">
                                  Aún no hay fiestas registradas
                                </Text>
                                <Text fontSize="xs" color={textColor} textAlign="center" maxW="md" mt={1}>
                                  Añade ferias, romerías o festividades para que las bandas conozcan el calendario
                                  municipal.
                                </Text>
                              </Flex>
                            ) : (
                              <VStack gap={3} align="stretch">
                                {[...(profileData.fiestasLocales || [])]
                                  .map((f, idx) => ({ f, idx }))
                                  .sort((a, b) => {
                                    const ai = (a.f.fechaInicio || '').trim() || '9999-12-31';
                                    const bi = (b.f.fechaInicio || '').trim() || '9999-12-31';
                                    return ai.localeCompare(bi);
                                  })
                                  .map(({ f, idx }) => {
                                    const rango = formatFiestaLocalRango(f);
                                    return (
                                      <Flex
                                        key={`${idx}-${f.nombre}`}
                                        align={{ base: 'stretch', sm: 'center' }}
                                        direction={{ base: 'column', sm: 'row' }}
                                        gap={3}
                                        p={4}
                                        rounded="xl"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                        bg={fiestaListRowBg}
                                        shadow="sm"
                                      >
                                        <Flex
                                          align="center"
                                          justify="center"
                                          w={{ base: 'full', sm: '52px' }}
                                          h="52px"
                                          minW={{ sm: '52px' }}
                                          rounded="lg"
                                          bg="purple.100"
                                          color="purple.600"
                                        >
                                          <Icon as={FaCalendarAlt} boxSize={6} />
                                        </Flex>
                                        <Box flex="1" minW={0}>
                                          <Text fontWeight="semibold" fontSize="md" lineClamp={2}>
                                            {f.nombre || 'Sin nombre'}
                                          </Text>
                                          {rango ? (
                                            <Badge mt={1} colorPalette="purple" variant="subtle" fontSize="xs">
                                              {rango}
                                            </Badge>
                                          ) : (
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                              Sin fechas indicadas
                                            </Text>
                                          )}
                                          {f.notas?.trim() ? (
                                            <Text fontSize="sm" color={textColor} mt={2} lineClamp={3}>
                                              {f.notas}
                                            </Text>
                                          ) : null}
                                        </Box>
                                        <HStack gap={1} alignSelf={{ base: 'flex-end', sm: 'center' }}>
                                          <Tooltip content="Editar">
                                            <IconButton
                                              aria-label="Editar fiesta"
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => openFiestaLocalModalEdit(idx)}><Icon as={FaEdit} /></IconButton>
                                          </Tooltip>
                                          <Tooltip content="Eliminar">
                                            <IconButton
                                              aria-label="Eliminar fiesta"
                                              size="sm"
                                              variant="ghost"
                                              colorPalette="red"
                                              onClick={() => removeFiestaLocal(idx)}><Icon as={FaTrash} /></IconButton>
                                          </Tooltip>
                                        </HStack>
                                      </Flex>
                                    );
                                  })}
                              </VStack>
                            )}
                          </Box>
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  )}

                  {profileData.perfilVariante === 'ASOCIACION_CULTURAL' && (
                    <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                      <Card.Header>
                        <Heading size="md">
                          <Icon as={FaMusic} mr={2} color="purple.600" />
                          Asociación cultural
                        </Heading>
                        <Text fontSize="sm" color={textColor}>
                          Ámbito y actividades de la entidad cultural.
                        </Text>
                      </Card.Header>
                      <Card.Body>
                        <VStack gap={4} align="stretch">
                          <Field.Root>
                            <Field.Label>Ámbito o disciplina</Field.Label>
                            <Input
                              value={profileData.culturalAmbito}
                              onChange={(e) => handleInputChange('culturalAmbito', e.target.value)}
                              placeholder="Ej: música de procesión, folklore, patrimonio…"
                            />
                          </Field.Root>
                          <Field.Root>
                            <Field.Label>Número de socios (aprox.)</Field.Label>
                            <NumberInput.Root
                              value={profileData.culturalNumeroSocios == null || profileData.culturalNumeroSocios === '' ? '' : String(profileData.culturalNumeroSocios)}
                              onValueChange={(details) =>
                                handleInputChange(
                                  'culturalNumeroSocios',
                                  details && typeof details === 'object' && 'value' in details ? details.value : (details ?? ''),
                                )
                              }
                              min={0}
                            >
                              <NumberInput.Input placeholder="Opcional" />
                              <NumberInput.Control>
                                <NumberInput.IncrementTrigger />
                                <NumberInput.DecrementTrigger />
                              </NumberInput.Control>
                            </NumberInput.Root>
                          </Field.Root>
                          <Field.Root>
                            <Field.Label>Actividades y proyectos</Field.Label>
                            <Textarea
                              value={profileData.culturalActividadesDescripcion}
                              onChange={(e) =>
                                handleInputChange('culturalActividadesDescripcion', e.target.value)
                              }
                              placeholder="Concursos, talleres, publicaciones, colaboraciones…"
                              rows={4}
                            />
                          </Field.Root>
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  )}

                  <CofradiaPerfilContactoPublico
                    cardBg={cardBg}
                    profileData={profileData}
                    handleNestedInputChange={handleNestedInputChange}
                    emailPlaceholder={cofradiaPerfilPlaceholders.email}
                  />
                </VStack>
              </SimpleGrid>

              {/* Localización (izq.) y redes sociales (dcha.); actividades solo perfil religioso */}
              <VStack gap={{ base: 6, md: 8 }} mt={{ base: 8, md: 10 }} w="full" align="stretch">
                <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 4, md: 6, lg: 8 }} w="full">
                  <CofradiaPerfilLocalizacion
                    cardBg={cardBg}
                    textColor={textColor}
                    profileData={profileData}
                    locationSearchQuery={locationSearchQuery}
                    setLocationSearchQuery={setLocationSearchQuery}
                    locationSuggestions={locationSuggestions}
                    showLocationSuggestions={showLocationSuggestions}
                    setShowLocationSuggestions={setShowLocationSuggestions}
                    isSearchingLocation={isSearchingLocation}
                    selectLocation={selectLocation}
                  />
                  <CofradiaPerfilRedesSociales
                    cardBg={cardBg}
                    profileData={profileData}
                    handleNestedInputChange={handleNestedInputChange}
                    redSocialHandlePlaceholder={cofradiaPerfilPlaceholders.redSocialHandle}
                  />
                  {profileData.perfilVariante === 'RELIGIOSO' && (
                    <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                      <Card.Header>
                        <Heading size="md">
                          <Icon as={FaChurch} mr={2} color="purple.600" />
                          Actividades y cultos
                        </Heading>
                        <Text fontSize="sm" color="gray.600">
                          Indica qué actividades religiosas y cultos realiza tu cofradía
                        </Text>
                      </Card.Header>
                      <Card.Body>
                        <SkillsEditor
                          title="Actividades y cultos"
                          helperText="Añade las actividades religiosas y cultos que realiza tu hermandad o cofradía"
                          placeholder="Ej: Semana Santa, Cultos Cuaresmales, Vía Crucis..."
                          skills={profileData.actividadesYCultos}
                          onSkillsChange={(newSkills) => handleInputChange('actividadesYCultos', newSkills)}
                          onSave={async (newSkills) => {
                            try {
                              await cofradiasAPI.update({ actividadesYCultos: newSkills });
                              handleInputChange('actividadesYCultos', newSkills);
                              showModal('¡Guardado!', 'Actividades y cultos actualizados correctamente.', 'success');
                            } catch (error) {
                              console.error('Error guardando actividades:', error);
                              showModal('Error', 'No se pudieron guardar las actividades. Inténtalo de nuevo.', 'error');
                              throw error;
                            }
                          }}
                          suggestions={actividadesSugeridas}
                          maxSkills={15}
                        />
                      </Card.Body>
                    </Card.Root>
                  )}
                </SimpleGrid>
              </VStack>

              {/* Galería de Fotos */}
              <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full" mt={{ base: 6, md: 8 }}>
                <Card.Header>
                  <HStack justify="space-between">
                    <Heading size="md">
                      <Icon as={FaCamera} mr={2} color="purple.600" />
                      Galería de fotos
                    </Heading>
                    <Text fontSize="sm" color={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'red.500' : 'gray.500'} fontWeight="medium">
                      {profileData.fotos?.length || 0}/{MAX_GALLERY_PHOTOS}
                    </Text>
                  </HStack>
                </Card.Header>
                <Card.Body>
                  <VStack gap={6}>
                    {/* Zona de Drag & Drop */}
                    <Box
                      w="full"
                      h="200px"
                      border="2px dashed"
                      borderColor={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'red.300' : isDragOver ? 'purple.400' : 'gray.300'}
                      borderRadius="lg"
                      bg={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'red.50' : isDragOver ? 'purple.50' : 'gray.50'}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 'not-allowed' : 'pointer'}
                      opacity={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? 0.6 : 1}
                      transition="all 0.3s ease"
                      onDragOver={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? undefined : handleDragOver}
                      onDragLeave={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? undefined : handleDragLeave}
                      onDrop={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? undefined : handleDrop}
                      onClick={() => {
                        if ((profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS) {
                          showModal('Límite alcanzado', `Has alcanzado el límite de ${MAX_GALLERY_PHOTOS} fotos. Elimina alguna para subir más.`, 'warning');
                          return;
                        }
                        galleryInputRef.current?.click();
                      }}
                      _hover={(profileData.fotos?.length || 0) >= MAX_GALLERY_PHOTOS ? {} : {
                        borderColor: 'purple.400',
                        bg: 'purple.50',
                        transform: 'translateY(-2px)',
                        shadow: 'lg'
                      }}
                      position="relative"
                      overflow="hidden"
                    >
                      {isUploading ? (
                        <VStack gap={3}>
                          <Progress.Root indeterminate colorPalette="purple" w="200px" rounded="full">
                            <Progress.Track>
                              <Progress.Range />
                            </Progress.Track>
                          </Progress.Root>
                          <Text color="purple.600" fontWeight="medium">
                            Procesando imágenes...
                          </Text>
                        </VStack>
                      ) : (
                        <VStack gap={3} textAlign="center">
                          <Icon 
                            as={FaCamera} 
                            w={12} 
                            h={12} 
                            color={isDragOver ? 'purple.500' : 'gray.400'}
                            transition="color 0.3s ease"
                          />
                          <VStack gap={1}>
                            <Text 
                              fontSize="lg" 
                              fontWeight="semibold" 
                              color={isDragOver ? 'purple.600' : 'gray.600'}
                            >
                              {isDragOver ? '¡Suelta las imágenes aquí!' : 'Arrastra y suelta tus fotos'}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              o haz clic para seleccionar archivos
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              JPG, PNG, WebP • Máx. 5MB por imagen • {profileData.fotos?.length || 0}/{MAX_GALLERY_PHOTOS} fotos
                            </Text>
                          </VStack>
                        </VStack>
                      )}
                      
                      <input
                        ref={galleryInputRef}
                        type="file"
                        hidden
                        multiple
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handlePhotoUpload}
                      />
                    </Box>

                    {/* Galería de Fotos Subidas */}
                    {profileData.fotos.length > 0 && (
                      <VStack gap={4} w="full">
                        <Separator />
                        <HStack gap={2} w="full" justify="space-between">
                          <Text fontSize="md" fontWeight="medium">
                            Fotos subidas ({profileData.fotos.length})
                          </Text>
                          {profileData.fotos.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              colorPalette="red"
                              onClick={onDeleteAllPhotosModalOpen}><Icon as={FaTrash} />Eliminar todas
                                                          </Button>
                          )}
                        </HStack>
                        
                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} gap={4} w="full">
                          {profileData.fotos.map((photo, index) => {
                            // photo puede ser un string (URL) o un objeto con url
                            const photoUrl = typeof photo === 'string' ? photo : photo.url;
                            const photoKey = typeof photo === 'string' ? photo : photo.id || index;
                            const resolvedPhotoUrl = resolveUploadsUrl(photoUrl);
                            
                            return (
                              <Box 
                                key={photoKey} 
                                position="relative" 
                                rounded="lg" 
                                overflow="hidden"
                                bg="white"
                                shadow="md"
                                _hover={{ shadow: 'xl', transform: 'translateY(-2px)' }}
                                transition="all 0.3s ease"
                              >
                                <Image
                                  src={resolvedPhotoUrl}
                                  alt="Foto del perfil"
                                  w="full"
                                  h="32"
                                  objectFit="cover"
                                  cursor="pointer"
                                  onClick={() => handleImageClick(resolvedPhotoUrl)} />

                                {/* Botón eliminar */}
                                <IconButton
                                  size="sm"
                                  colorPalette="red"
                                  variant="solid"
                                  position="absolute"
                                  top={2}
                                  right={2}
                                  onClick={() => removePhoto(photoUrl)}
                                  aria-label="Eliminar foto"
                                  shadow="lg"
                                  _hover={{ transform: 'scale(1.1)' }}
                                  transition="transform 0.2s ease"><Icon as={FaTrash} /></IconButton>
                              </Box>
                            );
                          })}
                        </SimpleGrid>
                      </VStack>
                    )}

                    {/* Mensaje informativo */}
                    <Box
                      bg="blue.50"
                      p={4}
                      rounded="lg"
                      border="1px solid"
                      borderColor="blue.200"
                      w="full"
                    >
                      <HStack gap={3}>
                        <Icon as={FaCamera} color="blue.500" />
                        <VStack gap={1} align="start">
                          <Text fontSize="sm" fontWeight="medium" color="blue.800">
                            Consejos para mejores fotos
                          </Text>
                          <Text fontSize="xs" color="blue.600">
                            {cofradiaPerfilPlaceholders.galeriaConsejoLinea1}
                            <br />
                            • Imágenes claras y bien iluminadas tienen mejor impacto
                            <br />
                            • Máximo 5MB por imagen para carga rápida
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  </VStack>
                </Card.Body>
              </Card.Root>

              {/* Videos de YouTube */}
              <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full" mt={{ base: 6, md: 8 }}>
                <Card.Header>
                  <HStack justify="space-between" align="center">
                    <Heading size="md">
                      <Icon as={FaYoutube} mr={2} color="red.500" />
                      Videos de YouTube
                    </Heading>
                    <Badge colorPalette="red" fontSize="xs">
                      {profileData.videos?.length || 0} videos
                    </Badge>
                  </HStack>
                </Card.Header>
                <Card.Body>
                  <VStack gap={6}>
                    {/* Formulario para añadir video */}
                    <Box w="full" p={4} bg="gray.50" rounded="lg">
                      <VStack gap={4}>
                        <Field.Root>
                          <Field.Label fontSize="sm">Título del video</Field.Label>
                          <Input
                            placeholder={cofradiaPerfilPlaceholders.youtubeTitulo}
                            value={newVideoTitle}
                            onChange={(e) => setNewVideoTitle(e.target.value)}
                            bg="white"
                          />
                        </Field.Root>
                        <Field.Root>
                          <Field.Label fontSize="sm">URL de YouTube</Field.Label>
                          <Input
                            placeholder="https://www.youtube.com/watch?v=..."
                            value={newVideoUrl}
                            onChange={(e) => setNewVideoUrl(e.target.value)}
                            bg="white"
                          />
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Formatos: youtube.com/watch?v=..., youtu.be/..., youtube.com/shorts/...
                          </Text>
                        </Field.Root>
                        <Button
                          colorPalette="red"
                          onClick={handleAddVideo}
                          disabled={!newVideoTitle.trim() || !newVideoUrl.trim()}
                          w="full"><Icon as={FaPlus} />Añadir Video
                                                  </Button>
                      </VStack>
                    </Box>

                    {/* Lista de videos */}
                    {profileData.videos && profileData.videos.length > 0 ? (
                      <VStack gap={4} w="full">
                        <HStack w="full" justify="space-between">
                          <Text fontSize="sm" fontWeight="medium" color="gray.600">
                            Videos añadidos ({profileData.videos.length})
                          </Text>
                          {profileData.videos.length > 0 && (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() => setProfileData(prev => ({ ...prev, videos: [] }))}><Icon as={FaTrash} />Eliminar todos
                                                          </Button>
                          )}
                        </HStack>
                        
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4} w="full">
                          {profileData.videos.map((video, index) => {
                            const videoId = extractYouTubeId(video.url);
                            const isShort = isYouTubeShort(video.url);
                            const thumbnailUrl = videoId
                              ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
                              : null;
                            
                            return (
                              <Box 
                                key={index} 
                                position="relative" 
                                rounded="lg" 
                                overflow="hidden"
                                bg="white"
                                shadow="md"
                                _hover={{ shadow: 'xl', transform: 'translateY(-2px)' }}
                                transition="all 0.3s ease"
                              >
                                {thumbnailUrl ? (
                                  <Box position="relative">
                                    <Image
                                      src={thumbnailUrl}
                                      alt={video.titulo}
                                      w="full"
                                      h={isShort ? '220px' : '140px'}
                                      objectFit="cover"
                                    />
                                    {isShort && (
                                      <Badge
                                        position="absolute"
                                        top={2}
                                        left={2}
                                        colorPalette="red"
                                        fontSize="2xs"
                                        rounded="md"
                                        px={1.5}
                                      >
                                        Short
                                      </Badge>
                                    )}
                                  </Box>
                                ) : (
                                  <Box w="full" h="140px" bg="gray.200" display="flex" alignItems="center" justifyContent="center">
                                    <Icon as={FaYoutube} w={12} h={12} color="gray.400" />
                                  </Box>
                                )}

                                {/* Overlay con título */}
                                <Box
                                  position="absolute"
                                  bottom={0}
                                  left={0}
                                  right={0}
                                  bg="blackAlpha.700"
                                  p={3}
                                >
                                  <Text fontSize="sm" fontWeight="medium" color="white" lineClamp={1}>
                                    {video.titulo}
                                  </Text>
                                </Box>

                                {/* Botón eliminar */}
                                <IconButton
                                  size="sm"
                                  colorPalette="red"
                                  variant="solid"
                                  position="absolute"
                                  top={2}
                                  right={2}
                                  onClick={() => handleRemoveVideo(index)}
                                  aria-label="Eliminar video"
                                  shadow="lg"
                                  _hover={{ transform: 'scale(1.1)' }}
                                  transition="transform 0.2s ease"><Icon as={FaTrash} /></IconButton>

                                {/* Botón play */}
                                <Box
                                  position="absolute"
                                  top="50%"
                                  left="50%"
                                  transform="translate(-50%, -50%)"
                                  bg="red.500"
                                  rounded="full"
                                  p={3}
                                  cursor="pointer"
                                  onClick={() => window.open(video.url, '_blank')}
                                  _hover={{ transform: 'translate(-50%, -50%) scale(1.1)', bg: 'red.600' }}
                                  transition="all 0.2s"
                                >
                                  <Icon as={FaPlay} color="white" w={4} h={4} />
                                </Box>
                              </Box>
                            );
                          })}
                        </SimpleGrid>
                      </VStack>
                    ) : (
                      <Box 
                        p={8} 
                        bg="gray.50" 
                        rounded="lg" 
                        textAlign="center"
                        w="full"
                      >
                        <Icon as={FaYoutube} w={12} h={12} color="gray.300" mb={3} />
                        <Text color="gray.500" fontWeight="medium">No hay videos añadidos</Text>
                        <Text fontSize="sm" color="gray.400">
                          Añade enlaces de YouTube para mostrar tus videos en el perfil
                        </Text>
                      </Box>
                    )}

                    {/* Mensaje informativo */}
                    <Box
                      bg="red.50"
                      p={4}
                      rounded="lg"
                      border="1px solid"
                      borderColor="red.200"
                      w="full"
                    >
                      <HStack gap={3}>
                        <Icon as={FaYoutube} color="red.500" />
                        <VStack gap={1} align="start">
                          <Text fontSize="sm" fontWeight="medium" color="red.800">
                            Consejos para videos
                          </Text>
                          <Text fontSize="xs" color="red.600">
                            • Comparte videos de procesiones, cultos, eventos especiales
                            <br />
                            • Asegúrate de que los videos sean públicos en YouTube
                            <br />
                            • Usa títulos descriptivos para que los visitantes sepan qué verán
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  </VStack>
                </Card.Body>
              </Card.Root>
              </VStack>
            </TabPanel>

            {/* Avales de Confianza */}
            <TabPanel value="1" px={0}>
              <VStack gap={{ base: 6, md: 8 }} align="stretch">
                {/* Explicación */}
                <Alert.Root status="info" rounded="xl" shadow="sm">
                  <Alert.Indicator />
                  <Box>
                    <Text fontWeight="bold">Otorga avales de confianza</Text>
                    <Text fontSize="sm">
                      Avala a las bandas con las que has trabajado anteriormente. Tu aval aparecerá en su perfil
                      y aumentará su credibilidad en la plataforma.
                    </Text>
                  </Box>
                </Alert.Root>

                {/* Buscar banda para avalar */}
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl">
                  <Card.Header>
                    <Heading size="lg">
                      <Icon as={FaHandshake} mr={3} color="green.600" />
                      Otorgar nuevo aval
                    </Heading>
                    <Text fontSize="sm" color={textColor} mt={2}>
                      Busca una banda para otorgarle un aval de confianza
                    </Text>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={4}>
                      <Field.Root>
                        <Field.Label>Buscar banda</Field.Label>
                        <HStack>
                          <Input
                            placeholder="Nombre de la banda..."
                            value={bandaSearchQuery}
                            onChange={(e) => setBandaSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearchBandas()}
                          />
                          <Button
                            colorPalette="purple"
                            onClick={handleSearchBandas}
                            loading={isSearchingBandas}><Icon as={FaSearch} />Buscar
                                                      </Button>
                        </HStack>
                      </Field.Root>

                      {/* Resultados de búsqueda */}
                      {bandasSearchResults.length > 0 && (
                        <VStack gap={2} align="stretch" w="full">
                          <Text fontWeight="bold" fontSize="sm">Resultados:</Text>
                          {bandasSearchResults.map((banda) => (
                            <Card.Root
                              key={banda.id}
                              bg={selectedBandaForAval?.id === banda.id ? 'green.50' : 'gray.50'}
                              shadow="sm"
                              rounded="lg"
                              cursor="pointer"
                              onClick={() => setSelectedBandaForAval(banda)}
                              borderWidth={selectedBandaForAval?.id === banda.id ? 2 : 0}
                              borderColor="green.400"
                              _hover={{ bg: 'green.50' }}
                            >
                              <Card.Body py={3}>
                                <HStack gap={3}>
                                  <Avatar.Root size="sm"><Avatar.Fallback name={banda.nombre} /><Avatar.Image src={banda.avatar} /></Avatar.Root>
                                  <VStack gap={0} align="start" flex={1}>
                                    <Text fontWeight="bold" fontSize="sm">{banda.nombre}</Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {banda.localizacion?.ciudad || 'Sin ubicación'}
                                    </Text>
                                  </VStack>
                                  {selectedBandaForAval?.id === banda.id && (
                                    <Icon as={FaCheck} color="green.500" />
                                  )}
                                </HStack>
                              </Card.Body>
                            </Card.Root>
                          ))}
                        </VStack>
                      )}

                      {/* Formulario de aval */}
                      {selectedBandaForAval && (
                        <VStack gap={4} w="full" p={4} bg="green.50" rounded="lg">
                          <HStack w="full">
                            <Avatar.Root size="md"><Avatar.Fallback name={selectedBandaForAval.nombre} /><Avatar.Image src={selectedBandaForAval.avatar} /></Avatar.Root>
                            <VStack gap={0} align="start" flex={1}>
                              <Text fontWeight="bold">{selectedBandaForAval.nombre}</Text>
                              <Text fontSize="sm" color="gray.600">
                                Seleccionada para avalar
                              </Text>
                            </VStack>
                          </HStack>
                          <Field.Root>
                            <Field.Label>Mensaje de aval (opcional)</Field.Label>
                            <Textarea
                              placeholder="Ej: Hemos trabajado con esta banda durante 10 años con excelentes resultados..."
                              value={avalTexto}
                              onChange={(e) => setAvalTexto(e.target.value)}
                              rows={3}
                            />
                            <Field.HelperText>
                              Este mensaje será visible en el perfil de la banda
                            </Field.HelperText>
                          </Field.Root>
                          <Button
                            colorPalette="green"
                            size="lg"
                            onClick={handleCreateAval}
                            loading={isCreatingAval}
                            loadingText="Otorgando aval..."
                            w="full"><Icon as={FaHandshake} />Otorgar aval
                                                      </Button>
                        </VStack>
                      )}
                    </VStack>
                  </Card.Body>
                </Card.Root>

                {/* Avales otorgados */}
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl">
                  <Card.Header>
                    <HStack>
                      <Heading size="lg">
                        <Icon as={FaAward} mr={3} color="yellow.500" />
                        Avales otorgados
                      </Heading>
                      {avalesOtorgados.length > 0 && (
                        <Badge colorPalette="green" fontSize="md" px={3} py={1} rounded="full">
                          {avalesOtorgados.length}
                        </Badge>
                      )}
                    </HStack>
                    <Text fontSize="sm" color={textColor} mt={2}>
                      Bandas a las que has avalado
                    </Text>
                  </Card.Header>
                  <Card.Body>
                    {isLoadingAvales ? (
                      <VStack py={8}>
                        <Progress.Root size="xs" indeterminate w="50%">
                          <Progress.Track>
                            <Progress.Range />
                          </Progress.Track>
                        </Progress.Root>
                        <Text color="gray.500">Cargando avales...</Text>
                      </VStack>
                    ) : avalesOtorgados.length === 0 ? (
                      <VStack gap={4} py={8}>
                        <Icon as={FaHandshake} w={16} h={16} color="gray.300" />
                        <Text fontSize="lg" color={textColor} textAlign="center">
                          No has otorgado ningún aval todavía
                        </Text>
                        <Text fontSize="md" color="gray.500" textAlign="center">
                          Busca una banda arriba para otorgarle un aval de confianza
                        </Text>
                      </VStack>
                    ) : (
                      <VStack gap={4} align="stretch">
                        {avalesOtorgados.map((aval) => (
                          <Card.Root
                            key={aval.id}
                            bg={aval.status === 'ACCEPTED' ? 'green.50' : aval.status === 'PENDING' ? 'yellow.50' : 'gray.50'}
                            shadow="sm"
                            rounded="lg"
                            borderLeft="4px solid"
                            borderColor={aval.status === 'ACCEPTED' ? 'green.400' : aval.status === 'PENDING' ? 'yellow.400' : 'gray.400'}
                          >
                            <Card.Body>
                              <HStack gap={4} justify="space-between" flexWrap="wrap">
                                <HStack gap={3} flex={1}>
                                  <Avatar.Root size="md"><Avatar.Fallback name={aval.banda?.nombre} /><Avatar.Image src={aval.banda?.avatar} /></Avatar.Root>
                                  <VStack gap={0} align="start" flex={1}>
                                    <Text fontWeight="bold">{aval.banda?.nombre}</Text>
                                    {aval.texto && (
                                      <Text fontSize="sm" color="gray.600" fontStyle="italic">
                                        "{aval.texto}"
                                      </Text>
                                    )}
                                    <Text fontSize="xs" color="gray.500">
                                      Creado el {new Date(aval.createdAt).toLocaleDateString('es-ES')}
                                    </Text>
                                  </VStack>
                                </HStack>
                                <HStack gap={2}>
                                  <Badge
                                    colorPalette={aval.status === 'ACCEPTED' ? 'green' : aval.status === 'PENDING' ? 'yellow' : 'red'}
                                    rounded="full"
                                    px={3}
                                  >
                                    {aval.status === 'ACCEPTED' ? 'Aceptado' : aval.status === 'PENDING' ? 'Pendiente' : 'Rechazado'}
                                  </Badge>
                                  {aval.status === 'PENDING' && (
                                    <Button
                                      size="sm"
                                      colorPalette="red"
                                      variant="ghost"
                                      onClick={() => handleRevocarAval(aval.id)}
                                    >
                                      Revocar
                                    </Button>
                                  )}
                                </HStack>
                              </HStack>
                            </Card.Body>
                          </Card.Root>
                        ))}
                      </VStack>
                    )}
                  </Card.Body>
                </Card.Root>
              </VStack>
            </TabPanel>

            {/* Reseñas */}
            <TabPanel value="2" px={{ base: 0, sm: 2 }}>
              {isMvpMode ? (
                <ComingSoonSection
                  title="Sistema de Reseñas - Próximamente"
                  message="El sistema de reseñas estará disponible muy pronto"
                  features={[
                    'Valoraciones automáticas tras cada evento',
                    'Puntuaciones en comunicación, formalidad y atención',
                    'Comentarios públicos y privados',
                    'Estadísticas de satisfacción',
                  ]}
                />
              ) : (
              <VStack gap={{ base: 6, md: 8 }}>
                <Alert.Root status="info" rounded="xl" shadow="sm">
                  <Alert.Indicator />
                  <Box>
                    <Text fontWeight="bold">Sistema de Reseñas</Text>
                    <Text fontSize="sm">
                      Cuando el evento termina (según su fecha y hora de fin), Cadencya te avisa por email y notificación para que puedas reseñar a la banda: tu opinión ayuda a mejorar y posicionar su perfil en la comunidad.
                    </Text>
                    <Text fontSize="sm" mt={2}>
                      Las valoraciones que dejas son <strong>anónimas</strong>. En el perfil público de la banda solo se muestra la <strong>puntuación total agregada</strong>; no se revela quién ha reseñado.
                    </Text>
                    <Text fontSize="sm" mt={2}>
                      Las reseñas que recibes de las bandas puedes publicarlas u ocultarlas en tu perfil; las publicadas son visibles para quien visite tu ficha.
                    </Text>
                  </Box>
                </Alert.Root>

                {/* Gestión de reseñas recibidas */}
                <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
                  <Card.Header>
                    <VStack align="start" gap={2} w="full">
                      <Heading size="lg">
                        <Icon as={FaComments} mr={3} color="purple.600" />
                        Gestión de reseñas recibidas
                      </Heading>
                      <Text fontSize="sm" color={textColor}>
                        Gestiona la visibilidad de las reseñas en tu perfil público. Solo las reseñas publicadas serán visibles.
                      </Text>
                      {profileData.resenas.length > 0 && (
                        <HStack gap={4} pt={2} fontSize="sm" color={textColor}>
                          <HStack>
                            <Icon as={FaGlobe} color="green.500" />
                            <Text fontWeight="medium">
                              Publicadas: {profileData.resenas.filter(r => r.isPublished).length}
                            </Text>
                          </HStack>
                          <HStack>
                            <Icon as={FaEyeSlash} color="orange.500" />
                            <Text fontWeight="medium">
                              Sin publicar: {profileData.resenas.filter(r => !r.isPublished).length}
                            </Text>
                          </HStack>
                        </HStack>
                      )}
                    </VStack>
                  </Card.Header>
                  <Card.Body>
                    {profileData.resenas.length === 0 ? (
                      <VStack gap={4} py={8}>
                        <Icon as={FaComments} w={16} h={16} color="gray.300" />
                        <Text fontSize="lg" color={textColor} textAlign="center">
                          Aún no has recibido reseñas
                        </Text>
                        <Text fontSize="md" color="gray.500" textAlign="center">
                          Las reseñas de las bandas con las que trabajes aparecerán aquí
                        </Text>
                      </VStack>
                    ) : (
                      <VStack gap={4} align="stretch">
                        {profileData.resenas
                          .sort((a, b) => new Date(b.receivedDate) - new Date(a.receivedDate))
                          .map((resena) => (
                            <Card.Root 
                              key={resena.id} 
                              bg={resena.isPublished ? reviewCardBg : 'orange.50'} 
                              shadow="md" 
                              rounded="lg"
                              borderLeft="4px solid"
                              borderColor={resena.isPublished ? 'green.400' : 'orange.400'}
                            >
                              <Card.Body>
                                <VStack gap={4} align="start" w="full">
                                  {/* Header con información de la banda y estado */}
                                  <HStack gap={3} w="full" justify="space-between" align="start">
                                    <HStack gap={3} flex={1}>
                                      <Avatar.Root size={{ base: "sm", md: "md" }}><Avatar.Fallback name={resena.bandaName} /><Avatar.Image src={resena.bandaAvatar} /></Avatar.Root>
                                      <VStack gap={0} align="start" flex={1}>
                                        <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                                          {resena.bandaName}
                                        </Text>
                                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                                          Recibida el {new Date(resena.receivedDate).toLocaleDateString('es-ES', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                          })}
                                        </Text>
                                      </VStack>
                                    </HStack>
                                    
                                    {/* Badge de estado */}
                                    <Badge
                                      colorPalette={resena.isPublished ? 'green' : 'orange'}
                                      variant="subtle"
                                      px={3}
                                      py={1}
                                      rounded="full"
                                      fontSize="xs"
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <Icon as={resena.isPublished ? FaGlobe : FaEyeSlash} />
                                      {resena.isPublished ? 'Publicada' : 'Sin publicar'}
                                    </Badge>
                                  </HStack>

                                  {/* Evento asociado */}
                                  {resena.eventoNombre && (
                                    <HStack gap={2} color="gray.600" fontSize="sm">
                                      <Icon as={FaCalendarAlt} />
                                      <Text>Evento: <strong>{resena.eventoNombre}</strong></Text>
                                      {resena.eventoFecha && (
                                        <Text>({new Date(resena.eventoFecha).toLocaleDateString('es-ES')})</Text>
                                      )}
                                    </HStack>
                                  )}
                                  
                                  {/* Valoraciones detalladas */}
                                  {resena.ratingGeneral && (
                                    <Box bg="gray.50" p={4} rounded="lg" w="full">
                                      <HStack gap={4} mb={3}>
                                        <HStack>
                                          {[1, 2, 3, 4, 5].map((star) => (
                                            <Icon
                                              key={star}
                                              as={star <= Math.round(resena.averageRating) ? FaStar : FaRegStar}
                                              color={star <= Math.round(resena.averageRating) ? 'yellow.400' : 'gray.300'}
                                              w={5}
                                              h={5}
                                            />
                                          ))}
                                        </HStack>
                                        <Text fontWeight="bold" fontSize="lg">{resena.averageRating.toFixed(1)}/5</Text>
                                      </HStack>
                                    </Box>
                                  )}
                                  
                                  {/* Comentario de la reseña */}
                                  {resena.comment && (
                                    <Box
                                      bg={reviewCommentBg}
                                      p={4}
                                      rounded="md"
                                      w="full"
                                      borderLeft="3px solid"
                                      borderColor={resena.isPublished ? 'green.300' : 'orange.300'}
                                    >
                                      <Text fontSize="md" color={textColor} fontStyle="italic">
                                        "{resena.comment}"
                                      </Text>
                                    </Box>
                                  )}
                                  
                                  {/* Acciones */}
                                  <Separator />
                                  <HStack gap={3} w="full" justify="space-between">
                                    <HStack gap={2}>
                                      <Button
                                        size="sm"
                                        colorPalette={resena.isPublished ? 'orange' : 'green'}
                                        variant="solid"
                                        onClick={() => handleTogglePublishReview(resena.id)}
                                        rounded="full"
                                        fontWeight="600"><Icon as={resena.isPublished ? FaEyeSlash : FaGlobe} />{resena.isPublished ? 'Despublicar' : 'Publicar'}</Button>
                                    </HStack>
                                    <Text fontSize="xs" color="gray.500">
                                      {resena.isPublished ? 'Visible en tu perfil público' : 'Oculta en tu perfil público'}
                                    </Text>
                                  </HStack>
                                </VStack>
                              </Card.Body>
                            </Card.Root>
                          ))}
                      </VStack>
                    )}
                  </Card.Body>
                </Card.Root>
              </VStack>
              )}
            </TabPanel>

            {/* Privado */}
            <TabPanel value="3" px={0}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 6, md: 8 }}>
                {/* Gestión de Datos y Cuenta (GDPR) */}
                <Card.Root bg={cardBg} shadow="md" rounded="2xl" border="1px solid" borderColor="blue.200">
                  <Card.Header pb={2}>
                    <HStack gap={3}>
                      <Icon as={FaShieldAlt} color="blue.500" w={5} h={5} />
                      <Heading size="md" color="blue.600">Gestión de Datos (GDPR)</Heading>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={4} align="stretch">
                      <Alert.Root status="info" rounded="md">
                        <Alert.Indicator />
                        <VStack align="start" gap={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            Tus derechos sobre tus datos
                          </Text>
                          <Text fontSize="xs">
                            Conforme al RGPD, tienes derecho a descargar, modificar y eliminar tus datos personales.
                          </Text>
                        </VStack>
                      </Alert.Root>
                      <Button
                        colorPalette="blue"
                        variant="outline"
                        onClick={handleExportData}
                        loading={isExporting}><Icon as={FaFileAlt} />Descargar mis datos
                                              </Button>
                    </VStack>
                  </Card.Body>
                </Card.Root>

                {/* Zona de Peligro */}
                <Card.Root bg={cardBg} shadow="md" rounded="2xl" border="1px solid" borderColor="red.200">
                  <Card.Header pb={2}>
                    <HStack gap={3}>
                      <Icon as={FaShieldAlt} color="red.500" w={5} h={5} />
                      <Heading size="md" color="red.600">Zona de Peligro</Heading>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={5} align="stretch">
                      {/* Desactivar cuenta */}
                      <Box p={4} bg="orange.50" rounded="md" border="1px solid" borderColor="orange.200">
                        <VStack align="start" gap={2}>
                          <Text fontSize="sm" fontWeight="bold" color="orange.700">
                            Desactivar cuenta
                          </Text>
                          <Text fontSize="xs" color="orange.600">
                            Tu cuenta quedará inactiva pero tus datos se conservarán. Podrás reactivarla contactando con soporte.
                          </Text>
                          <Button
                            colorPalette="orange"
                            variant="outline"
                            size="sm"
                            onClick={onDeactivateModalOpen}
                          >
                            Desactivar mi cuenta
                          </Button>
                        </VStack>
                      </Box>

                      <Separator />

                      {/* Eliminar permanentemente */}
                      <Box p={4} bg="red.50" rounded="md" border="1px solid" borderColor="red.200">
                        <VStack align="start" gap={2}>
                          <Text fontSize="sm" fontWeight="bold" color="red.700">
                            Eliminar cuenta permanentemente
                          </Text>
                          <Text fontSize="xs" color="red.600">
                            Se eliminarán todos tus datos personales de forma irreversible. Esta acción no se puede deshacer.
                          </Text>
                          <Button
                            colorPalette="red"
                            variant="solid"
                            size="sm"
                            onClick={onDeleteModalOpen}
                          >
                            Eliminar mi cuenta
                          </Button>
                        </VStack>
                      </Box>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              </SimpleGrid>
            </TabPanel>
          </TabPanels>
        </Tabs.Root>

        <ProfileTipsCarousel slides={profileTipsSlides} />
        </Container>
      </Box>

      {/* Modal de Éxito/Info */}
      <Dialog.Root open={isSaveModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onSaveModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon 
                    as={modalContent.type === 'success' ? FaCheckCircle : FaShieldAlt} 
                    color={modalContent.type === 'success' ? 'green.500' : 'blue.500'} 
                    w={6} 
                    h={6} 
                  />
                  <Text>{modalContent.title}</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={6}>
                <Text lineHeight="tall" color={textColor}>
                  {modalContent.message}
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <Button colorPalette="purple" onClick={onSaveModalClose}>
                  Entendido
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de Validación */}
      <Dialog.Root open={isVerificationModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onVerificationModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon as={FaShieldAlt} color="green.500" w={6} h={6} />
                  <Text>{modalContent.title}</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={6}>
                <VStack gap={4} align="start">
                  <Text lineHeight="tall" color={textColor}>
                    {modalContent.message}
                  </Text>
                  <Box bg="green.50" p={4} rounded="md" w="full">
                    <VStack gap={2} align="start">
                      <Text fontSize="sm" fontWeight="medium" color="green.800">
                        ¿Qué validamos?
                      </Text>
                      <VStack gap={1} align="start" fontSize="sm" color="green.700">
                        <Text>• Información de contacto válida</Text>
                        <Text>• Existencia legal de la cofradía</Text>
                        <Text>• Veracidad de los datos proporcionados</Text>
                        <Text>• Historial y reputación</Text>
                      </VStack>
                    </VStack>
                  </Box>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button colorPalette="green" onClick={onVerificationModalClose}>
                  Perfecto
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de Error */}
      <Dialog.Root open={isErrorModalOpen} placement='center' size='lg' onOpenChange={e => {
        if (!e.open) {
          onErrorModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.300" backdropFilter="blur(10px)" />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon as={FaIdCard} color="red.500" w={6} h={6} />
                  <Text>{modalContent.title}</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={6}>
                <VStack gap={4} align="start">
                  <Text lineHeight="tall" color={textColor}>
                    {modalContent.message}
                  </Text>
                  
                  {/* Lista de campos específicos clickeables */}
                  {modalContent.type === 'validation' && modalContent.missingFields && (
                    <Box bg="red.50" p={4} rounded="md" w="full">
                      <VStack gap={3} align="start">
                        <Text fontSize="sm" fontWeight="medium" color="red.800">
                          Campos faltantes (haz clic para ir al campo):
                        </Text>
                        <VStack gap={2} align="start" w="full">
                          {modalContent.missingFields.map((field, index) => (
                            <HStack
                              key={index}
                              gap={3}
                              w="full"
                              p={3}
                              bg="white"
                              rounded="md"
                              border="1px solid"
                              borderColor="red.200"
                              cursor="pointer"
                              _hover={{
                                bg: "red.50",
                                borderColor: "red.300",
                                transform: "translateY(-1px)",
                                shadow: "md"
                              }}
                              transition="all 0.2s ease"
                              onClick={() => {
                                navigateToField(field.ref, field.tab);
                                onErrorModalClose();
                              }}
                            >
                              <Icon as={FaExclamationTriangle} color="red.500" />
                              <VStack gap={0} align="start" flex={1}>
                                <Text fontSize="sm" fontWeight="medium" color="red.800">
                                  {field.name}
                                </Text>
                                <Text fontSize="xs" color="red.600">
                                  {field.section} • Tab {field.tab + 1}
                                </Text>
                              </VStack>
                              <Icon as={FaArrowRight} color="red.400" w={4} h={4} />
                            </HStack>
                          ))}
                        </VStack>
                        
                        <Box bg="blue.50" p={3} rounded="md" w="full" mt={2}>
                          <HStack gap={2}>
                            <Icon as={FaInfoCircle} color="blue.500" w={4} h={4} />
                            <Text fontSize="xs" color="blue.700">
                              <Text as="span" fontWeight="medium">Consejo:</Text> Al hacer clic en un campo, 
                              serás dirigido automáticamente a la pestaña y sección correspondiente.
                            </Text>
                          </HStack>
                        </Box>
                      </VStack>
                    </Box>
                  )}
                  
                  {/* Modal de error genérico */}
                  {modalContent.type === 'error' && modalContent.title === 'Información Incompleta' && (
                    <Box bg="red.50" p={4} rounded="md" w="full">
                      <VStack gap={2} align="start">
                        <Text fontSize="sm" fontWeight="medium" color="red.800">
                          Campos requeridos para validación:
                        </Text>
                        <VStack gap={1} align="start" fontSize="sm" color="red.700">
                          <Text>• Nombre de la cofradía</Text>
                          <Text>• Información del responsable completa</Text>
                          <Text>• Dirección completa (calle, ciudad, provincia)</Text>
                          <Text>• Datos de contacto válidos</Text>
                        </VStack>
                      </VStack>
                    </Box>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button colorPalette="red" variant="outline" onClick={onErrorModalClose}>
                  Entendido
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de Previsualización del perfil público */}
      <PublicProfilePreviewCofradia
        isOpen={showPublicPreview}
        onClose={() => setShowPublicPreview(false)}
        profileData={profileData}
        shareUrl={publicProfileShareUrl}
      />

      {/* Barra fija de cambios sin guardar con botón integrado */}
      {hasUnsavedChanges && (
        <Box
          position="fixed"
          top={{ base: '70px', md: '80px' }}
          left="50%"
          transform="translateX(-50%)"
          zIndex={1000}
          bg="orange.500"
          color="white"
          px={{ base: 3, md: 5 }}
          py={2}
          rounded="full"
          shadow="lg"
          display="flex"
          alignItems="center"
          gap={{ base: 2, md: 3 }}
          maxW="95vw"
        >
          <Icon as={FaExclamationTriangle} flexShrink={0} />
          <Text fontSize="sm" fontWeight="bold" display={{ base: 'none', sm: 'block' }}>
            Cambios sin guardar
          </Text>
          <Tooltip
            content={
              verificationStatus === 'pending'
                ? 'Perfil bloqueado mientras la validación está en proceso'
                : missingMandatoryLabels.length > 0
                  ? `Completa los apartados obligatorios: ${missingMandatoryLabels.join(', ')}`
                  : 'Guardar cambios del perfil'
            }
            showArrow><span>
              <span>
                <Button
                  size="sm"
                  bg="white"
                  color="orange.600"
                  fontWeight="700"
                  rounded="full"
                  onClick={handleSave}
                  loading={isLoading}
                  disabled={
                    verificationStatus === 'pending' || missingMandatoryLabels.length > 0
                  }
                  loadingText="Guardando..."
                  _hover={{ bg: 'orange.50', transform: 'scale(1.05)' }}
                  _active={{ transform: 'scale(0.95)' }}
                  transition="all 0.2s ease"><Icon as={FaSave} boxSize={3} />{verificationStatus === 'pending' ? 'Bloqueado' : 'Guardar'}</Button>
              </span>
            </span></Tooltip>
        </Box>
      )}

      {/* Modal de Desactivar cuenta */}
      <Dialog.Root open={isDeactivateModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onDeactivateModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon as={FaShieldAlt} color="red.500" w={6} h={6} />
                  <Text color="red.600">Desactivar cuenta</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={4}>
                <VStack gap={4} align="stretch">
                  <Alert.Root status="error" rounded="md">
                    <Alert.Indicator />
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" fontWeight="medium">
                        ¿Estás seguro de que quieres desactivar tu cuenta?
                      </Text>
                      <Text fontSize="xs">
                        • No podrás acceder a la plataforma{'\n'}
                        • Tu perfil dejará de ser visible{'\n'}
                        • Para reactivarla, contacta con soporte
                      </Text>
                    </VStack>
                  </Alert.Root>
                  <Field.Root >
                    <Field.Label>Confirma tu contraseña</Field.Label>
                    <Input
                      type="password"
                      placeholder="Introduce tu contraseña"
                      value={deactivatePassword}
                      onChange={(e) => setDeactivatePassword(e.target.value)}
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack gap={3}>
                  <Button variant="ghost" onClick={onDeactivateModalClose}>
                    Cancelar
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDeactivateAccount}
                    loading={isDeactivating}
                  >
                    Desactivar cuenta
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de Eliminar cuenta permanentemente */}
      <Dialog.Root open={isDeleteModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onDeleteModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon as={FaTrash} color="red.500" w={6} h={6} />
                  <Text color="red.600">Eliminar cuenta permanentemente</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={4}>
                <VStack gap={4} align="stretch">
                  <Alert.Root status="error" rounded="md">
                    <Alert.Indicator />
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" fontWeight="bold">
                        ⚠️ Esta acción es IRREVERSIBLE
                      </Text>
                      <Text fontSize="xs">
                        • Todos tus datos personales serán eliminados{'\n'}
                        • Tu perfil será anonimizado{'\n'}
                        • No podrás recuperar tu cuenta{'\n'}
                        • Los acuerdos existentes se mantendrán anonimizados
                      </Text>
                    </VStack>
                  </Alert.Root>
                  
                  <Field.Root >
                    <Field.Label>Confirma tu contraseña</Field.Label>
                    <Input
                      type="password"
                      placeholder="Introduce tu contraseña"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                    />
                  </Field.Root>

                  <Field.Root >
                    <Field.Label>Escribe ELIMINAR para confirmar</Field.Label>
                    <Input
                      placeholder="ELIMINAR"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack gap={3}>
                  <Button variant="ghost" onClick={onDeleteModalClose}>
                    Cancelar
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDeleteAccount}
                    loading={isDeleting}
                    disabled={deleteConfirmText !== 'ELIMINAR'}
                  >
                    Eliminar permanentemente
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Editor de imágenes (Cropper) */}
      <ImageCropper
        isOpen={isCropperOpen}
        onClose={() => {
          setIsCropperOpen(false);
          setCropperImage(null);
          setCropperType(null);
        }}
        imageSrc={cropperImage}
        onCropComplete={handleCropComplete}
        aspectRatio={cropperType === 'avatar' ? 1 : 16 / 9}
        cropShape={cropperType === 'avatar' ? 'round' : 'rect'}
        title={cropperType === 'avatar' ? 'Editar foto de perfil' : 'Editar imagen de fondo'}
      />

      {/* Modal de Cambios Sin Guardar */}
      <Dialog.Root open={isUnsavedChangesModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          cancelNavigation();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon as={FaExclamationTriangle} color="orange.500" w={6} h={6} />
                  <Text color="orange.600">Cambios sin guardar</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={4}>
                <VStack gap={4} align="stretch">
                  <Alert.Root status="warning" rounded="md">
                    <Alert.Indicator />
                    <Text fontSize="sm">
                      Tienes cambios sin guardar en tu perfil. Si sales ahora, perderás todos los cambios realizados.
                    </Text>
                  </Alert.Root>
                  <Text color="gray.600">
                    ¿Qué deseas hacer?
                  </Text>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack gap={3}>
                  <Button variant="ghost" onClick={confirmNavigation} colorPalette="red">
                    Salir sin guardar
                  </Button>
                  <Button colorPalette="purple" onClick={cancelNavigation}>
                    Continuar editando
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal para ver imagen en tamaño completo */}
      <Dialog.Root open={isImageModalOpen} size='full' placement='center' onOpenChange={e => {
        if (!e.open) {
          handleCloseImageModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.800" />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" boxShadow="none">
              <Dialog.CloseTrigger
                color="white"
                size="lg"
                bg="blackAlpha.600"
                _hover={{ bg: 'blackAlpha.700' }}
                rounded="full"
                m={4}
              />
              <Dialog.Body display="flex" alignItems="center" justifyContent="center" p={4}>
                {selectedImage && (
                  <Image
                    src={selectedImage}
                    alt="Imagen en tamaño completo"
                    maxH="90vh"
                    maxW="90vw"
                    objectFit="contain"
                  />
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de confirmación para eliminar todas las fotos */}
      <Dialog.Root open={isDeleteAllPhotosModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onDeleteAllPhotosModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Eliminar todas las fotos</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text>¿Estás seguro de que quieres eliminar todas las fotos? Esta acción no se puede deshacer.</Text>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onDeleteAllPhotosModalClose} disabled={isDeletingPhotos}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDeleteAllPhotos}
                  loading={isDeletingPhotos}
                  loadingText="Eliminando..."
                >
                  Eliminar todas
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal añadir/editar fiesta local (ayuntamiento) */}
      <Dialog.Root open={isFiestaLocalModalOpen} placement='center' size='lg' onOpenChange={e => {
        if (!e.open) {
          onFiestaLocalModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                {fiestaModalEditIndex == null ? 'Nueva festividad' : 'Editar festividad'}
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  <Field.Root required>
                    <Field.Label>Nombre</Field.Label>
                    <Input
                      value={fiestaModalDraft.nombre}
                      onChange={(e) => setFiestaModalDraft((d) => ({ ...d, nombre: e.target.value }))}
                      placeholder="Ej: Feria de agosto, Fiestas patronales…"
                    />
                  </Field.Root>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
                    <Field.Root>
                      <Field.Label>Fecha de inicio</Field.Label>
                      <Input
                        type="date"
                        value={fiestaModalDraft.fechaInicio}
                        onChange={(e) => setFiestaModalDraft((d) => ({ ...d, fechaInicio: e.target.value }))}
                      />
                      <Field.HelperText>Primer día de la festividad</Field.HelperText>
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Fecha de fin</Field.Label>
                      <Input
                        type="date"
                        value={fiestaModalDraft.fechaFin}
                        onChange={(e) => setFiestaModalDraft((d) => ({ ...d, fechaFin: e.target.value }))}
                      />
                      <Field.HelperText>Opcional: igual o posterior al inicio</Field.HelperText>
                    </Field.Root>
                  </SimpleGrid>
                  <Field.Root>
                    <Field.Label>Notas</Field.Label>
                    <Textarea
                      value={fiestaModalDraft.notas}
                      onChange={(e) => setFiestaModalDraft((d) => ({ ...d, notas: e.target.value }))}
                      rows={3}
                      placeholder="Ej: Conciertos en la plaza, pregón, recinto ferial…"
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onFiestaLocalModalClose}>
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={saveFiestaLocalFromModal}>
                  Guardar en el listado
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de cambio de username */}
      <Dialog.Root open={isUsernameModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onUsernameModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>Cambiar nombre de usuario</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={4}>
                  <Text fontSize="sm" color="gray.600">
                    Introduce tu nuevo nombre de usuario. Puedes cambiarlo un máximo de 2 veces al año.
                  </Text>
                  <Input
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="nuevo_username"
                  />
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onUsernameModalClose}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleChangeUsername}
                  loading={isChangingUsername}
                  disabled={!newUsername.trim()}
                >
                  Guardar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </AppLayout>
  );
};

export default ProfileCofradia;
